/**
 * The dungeon's adapter onto the shared combat kernel (PRD #784, slice E).
 *
 * Ability resolution does not live here any more. `castDungeonAbility` resolves
 * a catalog ability id, hands the cast to {@link requestCast}, and translates the
 * kernel's semantic events into the dungeon's effect stream. Cooldowns live in
 * each combatant's cooldown map, Aegis lives as a `damage-reduction` status, and
 * the four named fields that carried them are gone.
 *
 * Nothing here compares an ability id. Effects are selected by delivery shape,
 * which is a mechanical property of the catalog entry, and clips come from the
 * presentation descriptor. A guard in `dungeonCombat.abilityIds.test.ts` pins
 * that mechanically.
 *
 * Monsters go through the same door (PRD #793). A monster is a kernel combatant
 * with a loadout, a cooldown map, and statuses, its swing is a catalog ability,
 * and there is no `monster-attack` effect any more: a swing is the same generic
 * cast every other ability is. What is left here is the half a brain cannot do
 * on its own.
 *
 * *Perception in, intent out.* This module builds each monster's perception
 * snapshot - distances, line of sight, melee reachability, allies, readiness -
 * asks its brain, and executes the returned intent through the routing,
 * steering, and teleporter handling it already owned. A brain never touches the
 * world, and locomotion never makes a decision.
 *
 * *The caller's half of two kernel contracts.* A `persist` cast can only outlive
 * its caster while that combatant is registered, so a dead caster is held open
 * until its hazard lands; and the kernel reports a death rather than enacting
 * one, so a Hero killed by a monster's cast falls here.
 *
 * Mana and derived stats are dungeon run state that the kernel spends. The
 * adapter syncs them onto the player combatant at cast time and reads the
 * remaining mana back, which is the one place the two records meet.
 */

import { Direction } from './direction';
import type { DungeonBlueprint } from './dungeon';
import { type ProjectileEffectId } from './content';
import {
  archetypeBaseAtLevel,
  archetypeCombatStats,
  archetypeIdentity,
  arpgMonsterArchetype,
  type ArpgMonsterArchetype,
  type ArpgMonsterArchetypeId,
} from './arpgMonsters';
import {
  activateMemory,
  brain,
  createBrainMemory,
  type BrainId,
  type BrainIntent,
  type BrainMemory,
  type BrainPerception,
  type BrainPoint,
  type PerceivedAbility,
  type PerceivedTarget,
} from './combat/brains';
import { dungeonSightForLevel } from './dungeonSight';
import type { Entity, EntityId } from './entity';
import {
  createHero,
  heroCombatIdentity,
  type Hero,
  type HeroClassId,
  type HeroStats,
} from './hero';
import {
  ability as catalogAbility,
  abilityPresentation,
  type AbilityId,
  type AbilityShape,
} from './combat/abilityCatalog';
import {
  addCombatant,
  combatantFor,
  cooldownRemaining,
  deriveCombatStats,
  type CombatStats,
  type WeaponGrip,
} from './combat/combatant';
import {
  activeProjectiles,
  castFor,
  createAbilityRuntime,
  removeCombatant,
  requestCast,
  scaledDamage,
  stepAbilities,
  type AbilityRuntimeState,
  type CastRejection,
  type CombatEvent,
  type FrozenGeometry,
  type ProjectileHandle,
} from './combat/abilityRuntime';
import { effectiveModifiers } from './combat/statuses';
import { enactDeath, isDead } from './combat';
import { createHealth } from './health';
import { Mobile, type TilePoint } from './mobile';
import {
  advanceDungeonEnemyRoute,
  DUNGEON_ENEMY_COLLISION_RADIUS_TILES,
  DUNGEON_PLAYER_COLLISION_RADIUS_TILES,
  DUNGEON_PLAYER_COLLISION_SUBDIVISION_TILES,
  DUNGEON_PLAYER_RUN_SPEED_TILES_PER_SECOND,
  dungeonCellFor,
  integrateDungeonMovement,
  type DungeonMovementIntent,
} from './dungeonMovement';
import { spawnProjectile } from './projectile';
import { findRoute, sameLevelPrefix } from './routing';
import { teleporterCellKey, teleportersByCell } from './movementSystem';
import { canStepForLevel } from './walkability';
import type { DungeonChargeContact } from './combat/abilityShapes';
import type { World } from './world';
import type { CombatLabPlan, CombatLabScenario } from './combatLab';
import { arpgItemBaseOf, arpgItemName, type ArpgItem } from './arpgItem';
// The roller is imported straight from the content tree rather than through the
// reading seam, so describing an item never drags rolling one into the bundle.
import {
  arpgItemStream,
  rollArpgItem,
  type ArpgDropPosition,
} from './arpg/items/rolling';
import type { ArpgLootTableId } from './arpg/items/lootTables';
import type { SeededRng } from './rng';
import {
  ARPG_ELITE_DAMAGE_MULTIPLIER,
  ARPG_ELITE_HEALTH_MULTIPLIER,
  ARPG_ELITE_XP_MULTIPLIER,
  planArpgPopulation,
  type ArpgPopulationDiagnostics,
  type ArpgPopulationPlan,
  type ArpgRewardKind,
} from './arpgPopulation';
import type {
  CampaignObjective,
  CampaignObjectiveKind,
} from './arpg/campaign';

export const DUNGEON_MONSTER_REPATH_TICKS = 10;
export const DUNGEON_ACTION_MOVEMENT_TOLERANCE_MS = 60;

/**
 * How close a dormant monster has to be before the run thinks about it at all,
 * in tiles (PRD #794).
 *
 * A fully populated floor holds a couple of hundred monsters and the player can
 * only be near a handful of them. A monster that is neither engaged nor inside
 * this radius does no perception, no sight trace, no brain call, and no
 * steering: it costs one distance comparison per tick. The radius is a clear
 * margin past the twelve-tile detection radius, so nothing can be skipped in a
 * tick where it could have noticed the player.
 */
export const DUNGEON_DORMANT_ATTENTION_RADIUS_TILES = 20;

/**
 * How near two bodies have to be before either avoids the other, in tiles.
 *
 * Steering only cares about what it could collide with this tick, so the
 * obstacle list is built from the neighbourhood rather than from the floor. It
 * is what keeps a dense floor from costing the square of its population.
 */
const DUNGEON_OBSTACLE_RADIUS_TILES = 4;

/**
 * The bright the elite treatment paints a promoted monster, from the named dye
 * strip. Presentation only: an elite is identifiable at a glance and is
 * otherwise the same monster in the same role (PRD #794).
 */
export const DUNGEON_ELITE_HUE = 1161;

/**
 * The shadow a summoned combatant is painted, so a Necromancer's skeleton is
 * told apart from a hostile one at a glance (PRD #804). Presentation only,
 * like the elite hue, and matching the class's own shadow-dyed look.
 */
export const DUNGEON_SUMMON_HUE = 1109;

/** How much one health globe restores, as a share of maximum health. */
export const DUNGEON_HEALTH_GLOBE_SHARE = 0.08;

/** How close the player has to be to collect a globe by contact, in tiles. */
export const DUNGEON_HEALTH_GLOBE_PICKUP_TILES = 1.2;

/**
 * The deterministic sustain rule: one globe every this many ordinary kills, and
 * one from every elite regardless. Density is only survivable if killing things
 * pays health back, and a rule is what keeps that reproducible from a seed.
 */
export const DUNGEON_HEALTH_GLOBE_KILL_INTERVAL = 3;

/** The deterministic ordinary item drop rule. Elites always drop. */
export const DUNGEON_ITEM_DROP_KILL_INTERVAL = 12;

/**
 * The one dungeon's rung on the material ladder (PRD #795).
 *
 * The drop window is `[N-1, N, N+1]`, so a second rung is what makes both ends
 * of the window reachable: this floor pays mostly Dull Copper, some Normal, and
 * the occasional Shadow. Campaign dungeons declare their own tier as they are
 * authored; assigning a tier per dungeon is out of this PRD's scope.
 */
export const DUNGEON_MATERIAL_TIER = 2;

/**
 * The item level a cache pays at, when no monster's level answers the question.
 *
 * A guarded cache is not a kill, so it has no archetype to read a level from.
 * It pays at the floor's own depth, which for the one dungeon that exists is
 * the level its population is authored at.
 */
export const DUNGEON_CACHE_ITEM_LEVEL = 1;

/**
 * The archetype the dungeon's adventure encounter spawns.
 *
 * The one place the dungeon names a monster, and it names an archetype rather
 * than a bestiary row, a body, or a set of numbers. Which archetypes go where,
 * how many, and in what mix is the encounter population PRD's (#794).
 */
export const DUNGEON_ADVENTURE_ARCHETYPE_ID: ArpgMonsterArchetypeId =
  'skeleton-warrior';

function initialWarriorIdentity() {
  const hero = createHero('warrior');
  const identity = hero && heroCombatIdentity(hero);
  if (!identity) throw new Error('Warrior Hero definition is invalid');
  return identity;
}

const INITIAL_WARRIOR_IDENTITY = initialWarriorIdentity();
const INITIAL_WARRIOR_ABILITY = INITIAL_WARRIOR_IDENTITY.abilities[0];
if (!INITIAL_WARRIOR_ABILITY) throw new Error('Warrior Hero has no level-one ability');

const DUNGEON_ADVENTURE_ARCHETYPE = (() => {
  const archetype = arpgMonsterArchetype(DUNGEON_ADVENTURE_ARCHETYPE_ID);
  if (!archetype) throw new Error('the dungeon adventure archetype is not declared');
  return archetype;
})();

export const DUNGEON_WARRIOR_LOADOUT = {
  classId: INITIAL_WARRIOR_IDENTITY.classId,
  level: INITIAL_WARRIOR_IDENTITY.level,
  maxHealth: INITIAL_WARRIOR_IDENTITY.stats.maxHealth,
  maxMana: INITIAL_WARRIOR_IDENTITY.stats.maxMana,
  mainHand: INITIAL_WARRIOR_IDENTITY.mainHand,
  equipment: INITIAL_WARRIOR_IDENTITY.equipment,
  ability: INITIAL_WARRIOR_ABILITY,
  /**
   * The encounter the adventure spawns, read off the archetype rather than
   * declared here. It is what a Hero is tuned against, so it stays reachable
   * from the loadout the tests measure against, but the numbers live with the
   * monster now.
   */
  monster: {
    id: DUNGEON_ADVENTURE_ARCHETYPE.bestiaryId,
    archetype: DUNGEON_ADVENTURE_ARCHETYPE.id,
    maxHealth: DUNGEON_ADVENTURE_ARCHETYPE.base.maxHealth,
    damage: DUNGEON_ADVENTURE_ARCHETYPE.base.mainHandDamage,
    msPerTile: DUNGEON_ADVENTURE_ARCHETYPE.msPerTile,
  },
} as const;

export interface DungeonCombatState {
  player: EntityId;
  /** Exact ARPG-space position. The Mobile anchor remains integer collision state. */
  playerPosition: { x: number; y: number };
  playerAnchor: TilePoint;
  movement: {
    seq: number;
    intent: DungeonMovementIntent;
    /** Exact position where the current intent began, used to settle edge checkpoints. */
    origin: { x: number; y: number };
    /** Simulated time under the current intent, used for prediction replay. */
    elapsedMs: number;
    /** Charge owns locomotion for its short presentation window. */
    chargeRemainingMs: number;
    /** Partial movement integrated early for an ordered action checkpoint. */
    prepaidMs: number;
    /** A dungeon teleporter moved the authority and requires a client re-anchor. */
    repositioned: boolean;
  };
  /** The primary live target retained for the single-monster adventure interface. */
  monster: EntityId;
  /** Every currently living target. Ordinary runs contain exactly one. */
  monsters: EntityId[];
  /** Grid routes consumed by the server-owned continuous enemy steering layer. */
  monsterRoutes: Map<EntityId, TilePoint[]>;
  /**
   * What each living monster is, as an archetype id (PRD #793).
   *
   * The archetype is where its stats, its loadout, and its brain come from, so
   * the parallel `monsterDamage` map that used to sit here is gone: damage is on
   * the combatant's derived stats like everybody else's.
   */
  monsterArchetypes: Map<EntityId, ArpgMonsterArchetypeId>;
  /**
   * Each monster's brain memory: who it is fighting, where it last saw them,
   * and how long ago. Owned here rather than inside a brain because a brain is a
   * pure policy that gets handed its memory and hands back the next one.
   */
  monsterMemory: Map<EntityId, BrainMemory>;
  /** Where each monster's brain last asked to stand, so a route is rebuilt only when it moves. */
  monsterGoals: Map<EntityId, TilePoint>;
  /**
   * Dead monsters whose cast is still owed to the player.
   *
   * A `persist` caster-death policy means a committed hazard resolves whether or
   * not its caster survives to see it, and the kernel can only honour that while
   * the combatant is still registered. These are stepped and then released the
   * tick their cast runs out.
   */
  orphanedCasters: EntityId[];
  /**
   * Every living summoned combatant, in summon order (PRD #804).
   *
   * A summon is on the player's team and is deliberately not a monster: it
   * pays no XP, rolls no loot, counts no kill, and belongs to no pack. Its
   * routes, goals, and brain memory live in the same maps the monsters use,
   * because those are keyed storage and the steering that reads them is
   * shared.
   */
  summons: EntityId[];
  /** The brain each summon runs, from its summon payload's declaration. */
  summonBrains: Map<EntityId, BrainId>;
  mode: 'adventure' | 'combat-lab';
  classId: HeroClassId;
  level: number;
  xp: number;
  /**
   * The Hero's allocated points, carried for presentation and settlement. No
   * combat code reads it: every number combat needs is on {@link stats}.
   */
  allocated: HeroStats;
  /**
   * The derived record (PRD #792): vitals, damage scalars, both speed scalars,
   * crit, regeneration, and resists. It replaced the four flat fields that used
   * to sit here, and it is the only thing the adapter hands the kernel.
   */
  stats: CombatStats;
  /** The default ability a bare cast command resolves to. */
  ability: AbilityId;
  abilities: AbilityId[];
  mana: number;
  /** Fractional regeneration carried between ticks, so a rate below one point per tick still lands. */
  manaRegenProgress: number;
  healthRegenProgress: number;
  /**
   * The kernel's runtime state for this run: combatants, their cooldown maps,
   * their statuses, and any cast in flight. It replaces the four per-ability
   * fields that used to sit alongside it, and it is owned here rather than
   * hidden inside the kernel so one run cannot leak into another.
   */
  combat: AbilityRuntimeState;
  /**
   * The world entity drawing each shot the kernel has in the air (PRD #800).
   *
   * The kernel simulates the flight and knows nothing about art, so the adapter
   * is what gives a shot a body on the wire: it spawns the entity when the
   * kernel reports a launch, moves it to wherever the kernel says the shot is,
   * and removes it when the shot ends. Nothing else steps these entities, which
   * is what keeps one authority over where an arrow is.
   */
  projectileEntities: Map<ProjectileHandle, EntityId>;
  elapsedTicks: number;
  kills: number;
  dyingMonsters: { id: EntityId; remainingTicks: number }[];
  monsterPathCooldownTicks: number;
  combatLab?: {
    scenario: CombatLabScenario;
    spawnTiles: TilePoint[];
    spawnCursor: number;
  };
  runId: string;
  backpack: ArpgItem[];
  /**
   * Pack membership (PRD #794).
   *
   * A pack is the unit activation travels through: striking one member alerts
   * the pack it belongs to and nothing else, which is what makes a two-pack pull
   * something the player caused rather than something the floor did.
   */
  packs: Map<string, DungeonPackState>;
  monsterPacks: Map<EntityId, string>;
  /** Promoted members. Bounded multipliers, better rewards, unchanged role. */
  elites: Set<EntityId>;
  /** Designated rewarded dead ends and the guardians a cache waits on. */
  rewards: DungeonRewardState[];
  /** Ordinary kills since the last globe, for the deterministic sustain rule. */
  killsSinceGlobe: number;
  /** Ordinary kills since the last item, for the deterministic drop rule. */
  killsSinceDrop: number;
  /**
   * The run's dedicated item stream (PRD #795).
   *
   * Item luck is drawn here and nowhere else, so rolling one more drop can
   * never move a pack anchor, an elite promotion, or a corridor. The world rng
   * that combat draws its crits from is a different stream again, which is why
   * a reported floor stays reproducible even though a fight is not.
   */
  itemRng: SeededRng;
  /** How many items this run has rolled, which is what makes a drop id unique. */
  drops: number;
  /** The dungeon's rung on the material ladder: the `N` of the drop window. */
  materialTier: number;
  /** What the planner produced for this floor, for admin-visible diagnostics. */
  population?: ArpgPopulationDiagnostics;
  /**
   * The campaign node objective this run tracks (PRD #837 slice 2), absent for
   * a run that is not a campaign node. It gates advancement and nothing else:
   * the exit stays usable at any clear percentage, objective met or not.
   */
  objective?: DungeonObjectiveState;
}

/**
 * One campaign objective, live in a run.
 *
 * `slay-guardians` rides the named-guardian-pack mechanism the guarded cache
 * proves: every designated pack must be wholly dead. `slay-boss` keys on the
 * declared boss archetype's death, so the boss archetype (slice 5) needs no
 * special field here: it is an archetype id like any monster's.
 */
export type DungeonObjectiveState =
  | {
      nodeId: string;
      kind: 'slay-guardians';
      /** The named packs whose last death settles the objective. */
      guardianPackIds: string[];
      complete: boolean;
    }
  | {
      nodeId: string;
      kind: 'slay-boss';
      bossArchetypeId: string;
      complete: boolean;
    };

/** One planned pack, as it exists in a live run. */
export interface DungeonPackState {
  id: string;
  template: string;
  /** Every member spawned for this pack, living or dead. */
  members: EntityId[];
  /** The reward this pack guards, when it guards one. */
  guards?: string;
}

/** One designated rewarded dead end, as it exists in a live run. */
export interface DungeonRewardState {
  id: string;
  kind: ArpgRewardKind;
  /** The cache entity, for a guarded cache. */
  container?: EntityId;
  guardianPacks: string[];
  /** True once every guardian pack is dead and the cache will open. */
  unlocked: boolean;
}

export type StartDungeonCombatOptions =
  | {
      mode?: 'adventure';
      combatLab?: never;
      /**
       * The dungeon's rung on the material ladder (PRD #795), the `N` of the
       * `[N-1, N, N+1]` drop window. Campaign dungeons declare their own; the
       * one dungeon that exists uses {@link DUNGEON_MATERIAL_TIER}.
       */
      materialTier?: number;
      /**
       * An explicit population plan, replacing the one this run would derive
       * from its blueprint (PRD #794).
       *
       * The planner is a pure function, so a caller that already has a plan -
       * a replay of a reported floor, or a test that wants one known pack on a
       * real floor - hands it over rather than reproducing the draw order.
       */
      population?: ArpgPopulationPlan;
      /**
       * The campaign node this run plays (PRD #837 slice 2).
       *
       * The objective comes from the declared node; the optional guardian
       * designation is for authored-map sidecars (slice 4) that name their
       * guardian packs, and a procedural run derives its own from the plan.
       */
      campaign?: {
        nodeId: string;
        objective: CampaignObjective;
        guardianPackIds?: readonly string[];
      };
    }
  | {
      mode: 'combat-lab';
      combatLab: CombatLabPlan;
      materialTier?: number;
    };

export type DungeonCastRejection = 'cooldown' | 'mana' | 'dead' | 'aim' | 'ability';

export type DungeonCastResult =
  | { accepted: true; effects: DungeonCombatEffect[] }
  | { accepted: false; reason: DungeonCastRejection };

function cellFor(point: { x: number; y: number }): TilePoint {
  return dungeonCellFor(point);
}

function sameCell(a: TilePoint, b: TilePoint): boolean {
  return a.x === b.x && a.y === b.y;
}

/**
 * One spawn's tuning: an archetype, an optional promotion, plus the overrides an
 * authored Combat Lab scenario may put on top of it so a lane target can be made
 * harmless or a pressure target durable without declaring a new archetype for
 * each.
 */
export interface DungeonMonsterSpawn {
  archetype: ArpgMonsterArchetypeId;
  name?: string;
  maxHealth?: number;
  damage?: number;
  msPerTile?: number;
  /**
   * The level this monster fights at (PRD #837 slice 7): the population
   * plan's declared `monsterLevel`, scaling the archetype's vitals and damage
   * band through `archetypeBaseAtLevel` before any promotion or override.
   * Absent, the archetype fights at its declared base level.
   */
  level?: number;
  /**
   * A promotion (PRD #794). It multiplies the vitals and the damage band, paints
   * the body, and renames it, and it changes nothing else: an elite carries the
   * same loadout, runs the same brain, and holds the same place in its pack.
   */
  elite?: boolean;
}

function spawnTuning(spawn: DungeonMonsterSpawn): {
  archetype: ArpgMonsterArchetype;
  name: string;
  level: number;
  maxHealth: number;
  damage: number;
  msPerTile: number;
  elite: boolean;
} {
  const archetype =
    arpgMonsterArchetype(spawn.archetype) ?? DUNGEON_ADVENTURE_ARCHETYPE;
  const identity = archetypeIdentity(archetype);
  const elite = spawn.elite === true;
  const name = spawn.name ?? identity.name;
  const base = archetypeBaseAtLevel(
    archetype,
    spawn.level ?? archetype.base.level,
  );
  const maxHealth = spawn.maxHealth ?? base.maxHealth;
  const damage = spawn.damage ?? base.mainHandDamage;
  return {
    archetype,
    name: elite ? `Elite ${name}` : name,
    level: base.level,
    maxHealth: elite
      ? Math.round(maxHealth * ARPG_ELITE_HEALTH_MULTIPLIER)
      : maxHealth,
    damage: elite ? Math.round(damage * ARPG_ELITE_DAMAGE_MULTIPLIER) : damage,
    msPerTile: spawn.msPerTile ?? archetype.msPerTile,
    elite,
  };
}

/**
 * Put one archetype's body in the world.
 *
 * Everything visible comes from the bestiary row the archetype names, so a
 * Ratman Archer spawns with the Ratman Archer's body, hue, and name rather than
 * with a skeleton wearing different numbers, which is what this function did
 * before archetypes existed.
 */
function spawnDungeonMonster(
  world: World,
  at: TilePoint,
  spawn: DungeonMonsterSpawn,
) {
  const tuning = spawnTuning(spawn);
  const identity = archetypeIdentity(tuning.archetype);
  return world.createEntity({
    position: { ...at, facing: Direction.S },
    mobile: new Mobile({
      ...at,
      facing: Direction.S,
      msPerTile: tuning.msPerTile,
    }),
    renderable: {
      body: identity.body,
      ...(tuning.elite
        ? { bodyHue: DUNGEON_ELITE_HUE }
        : identity.hue !== undefined
          ? { bodyHue: identity.hue }
          : {}),
      name: tuning.name,
    },
    health: {
      ...createHealth(tuning.maxHealth),
      regenPaused: true,
    },
    dungeonEnemy: true,
    interaction: { role: 'scenery' },
  });
}

/**
 * The documented summon placement order: the eight cells around the caster,
 * cardinals first, then the ring at distance two, first legal cell wins. Legal
 * means walkable, steppable from the caster's cell, and not under any
 * combatant's body. No rng is drawn, so the same field always raises the same
 * skeleton on the same tile (PRD #804).
 */
const DUNGEON_SUMMON_OFFSETS: readonly { x: number; y: number }[] = [
  { x: 1, y: 0 },
  { x: 0, y: 1 },
  { x: -1, y: 0 },
  { x: 0, y: -1 },
  { x: 1, y: 1 },
  { x: -1, y: 1 },
  { x: -1, y: -1 },
  { x: 1, y: -1 },
  { x: 2, y: 0 },
  { x: 0, y: 2 },
  { x: -2, y: 0 },
  { x: 0, y: -2 },
];

function dungeonSummonTile(
  world: World,
  state: DungeonCombatState,
  origin: { x: number; y: number },
): TilePoint | null {
  const anchor = cellFor(origin);
  const occupied = new Set(
    [state.player, ...state.monsters, ...state.summons].flatMap((id) => {
      const position = world.get(id)?.position;
      if (!position) return [];
      const cell = cellFor(position);
      return [`${cell.x},${cell.y}`];
    }),
  );
  for (const offset of DUNGEON_SUMMON_OFFSETS) {
    const tile = { x: anchor.x + offset.x, y: anchor.y + offset.y };
    if (!world.map.isWalkable(tile.x, tile.y)) continue;
    if (!world.map.canStep(anchor, tile)) continue;
    if (occupied.has(`${tile.x},${tile.y}`)) continue;
    return tile;
  }
  return null;
}

/**
 * A summoned combatant's body: the archetype's bestiary identity under the
 * summon shadow. It carries the same `dungeonEnemy` marker every dungeon
 * combatant body carries, because the marker means "a body the run steps
 * continuously" to the movement system and the renderer, not "hostile":
 * hostility lives on the combatant's team.
 */
function spawnDungeonSummonBody(
  world: World,
  at: TilePoint,
  archetype: ArpgMonsterArchetype,
) {
  const identity = archetypeIdentity(archetype);
  return world.createEntity({
    position: { ...at, facing: Direction.S },
    mobile: new Mobile({
      ...at,
      facing: Direction.S,
      msPerTile: archetype.msPerTile,
    }),
    renderable: {
      body: identity.body,
      bodyHue: DUNGEON_SUMMON_HUE,
      name: identity.name,
    },
    health: {
      ...createHealth(archetype.base.maxHealth),
      regenPaused: true,
    },
    dungeonEnemy: true,
    interaction: { role: 'scenery' },
  });
}

function livingDungeonSummons(world: World, state: DungeonCombatState) {
  return state.summons.flatMap((id) => {
    const entity = world.get(id);
    return entity?.position && entity.health && !isDead(entity) ? [entity] : [];
  });
}

/**
 * Adopt one freshly summoned combatant into the run: membership, its declared
 * brain, and the same steering records a monster gets. The kernel already
 * registered the combatant and stamped the bond; this is the caller's half.
 */
function adoptDungeonSummon(
  state: DungeonCombatState,
  summon: EntityId,
  brainId: BrainId,
): void {
  state.summons.push(summon);
  state.summonBrains.set(summon, brainId);
  state.monsterMemory.set(summon, createBrainMemory());
  state.monsterRoutes.set(summon, []);
}

/**
 * Forget a summon that left play, however it left: killed, expired, replaced,
 * or dissipated with its owner. The body crumbles through the same dying
 * window a monster gets, so the death pose plays, and nothing here pays XP,
 * rolls loot, or counts a kill, because a summon is not a monster.
 */
function releaseDungeonSummon(
  world: World,
  state: DungeonCombatState,
  summon: EntityId,
  effects: DungeonCombatEffect[],
): void {
  if (!state.summons.includes(summon)) return;
  state.summons = state.summons.filter((id) => id !== summon);
  state.summonBrains.delete(summon);
  state.monsterMemory.delete(summon);
  state.monsterRoutes.delete(summon);
  state.monsterGoals.delete(summon);
  removeCombatant(state.combat, summon);
  if (world.get(summon)) {
    state.dyingMonsters.push({ id: summon, remainingTicks: 20 });
    effects.push({ kind: 'monster-die', monster: summon });
  }
}

export function startDungeonCombat(
  world: World,
  blueprint: DungeonBlueprint,
  playerId: EntityId,
  hero: Hero,
  runId = `dungeon:${world.rng.getState()}`,
  stash: readonly ArpgItem[] = [],
  options: StartDungeonCombatOptions = {},
): DungeonCombatState {
  const player = world.get(playerId);
  if (!player?.renderable || !player.health || !player.position) {
    throw new Error('dungeon combat requires a rendered player with health');
  }
  const identity = heroCombatIdentity(hero, stash);
  if (!identity) throw new Error('dungeon combat requires a valid playable Hero');
  const ability = identity.abilities[0];
  if (!ability) throw new Error('dungeon combat requires an unlocked Hero ability');
  player.health = {
    ...createHealth(identity.stats.maxHealth),
    regenPaused: true,
  };
  player.renderable.equipment = identity.equipment;

  const mode = options.mode ?? 'adventure';
  const combatLab = options.mode === 'combat-lab' ? options.combatLab : undefined;
  const spawnTiles = combatLab
    ? combatLab.monsterSpawns.map((spawn) => ({ ...spawn }))
    : [];
  const packSize = combatLab
    ? Math.min(combatLab.packSize, spawnTiles.length)
    : 0;
  if (combatLab && (spawnTiles.length === 0 || packSize < 1)) {
    throw new Error('dungeon combat requires at least one monster spawn');
  }
  const enemySpawn: DungeonMonsterSpawn = combatLab
    ? scenarioSpawn(combatLab.scenario)
    : { archetype: DUNGEON_ADVENTURE_ARCHETYPE.id };
  /**
   * The whole ordinary population, planned before the run begins (PRD #794).
   *
   * The planner is a pure function of the blueprint and its seed, so what a
   * floor holds is reproducible from the seed a player reports, and every
   * monster on it exists before the player takes a step: nothing pops into
   * being in front of anybody.
   */
  const population = combatLab
    ? null
    : ('population' in options ? options.population : undefined) ??
      planArpgPopulation({ blueprint });
  const placements: {
    at: TilePoint;
    spawn: DungeonMonsterSpawn;
    packId?: string;
  }[] = combatLab
    ? spawnTiles
        .slice(0, packSize)
        .map((at) => ({ at, spawn: enemySpawn }))
    : (population?.packs ?? []).flatMap((pack) =>
        pack.members.map((member) => ({
          at: member.position,
          spawn: {
            archetype: member.archetype,
            // The plan's declared level (PRD #837 slice 7) rides every spawn,
            // so a tier's strength is the plan's own statement, not a branch.
            ...(population?.monsterLevel !== undefined
              ? { level: population.monsterLevel }
              : {}),
            ...(member.elite ? { elite: true } : {}),
          },
          packId: pack.id,
        })),
      );
  const spawned = placements.map((placement) =>
    spawnDungeonMonster(world, placement.at, placement.spawn),
  );

  const combat = createAbilityRuntime();
  addCombatant(combat.combatants, {
    entity: playerId,
    team: 'player',
    loadout: identity.abilities,
    stats: identity.stats,
    maxMana: identity.stats.maxMana,
  });

  const state: DungeonCombatState = {
    player: playerId,
    playerPosition: { x: player.position.x, y: player.position.y },
    playerAnchor: {
      x: player.mobile?.tileX ?? Math.round(player.position.x),
      y: player.mobile?.tileY ?? Math.round(player.position.y),
    },
      movement: {
        seq: 0,
        intent: { dir: null, running: false },
        origin: {
          x: player.position.x,
          y: player.position.y,
        },
        elapsedMs: 0,
      chargeRemainingMs: 0,
      prepaidMs: 0,
      repositioned: false,
    },
    monster: spawned[0]?.id ?? (0 as EntityId),
    monsters: spawned.map((candidate) => candidate.id),
    monsterRoutes: new Map(
      spawned.map((candidate) => [candidate.id, []]),
    ),
    monsterArchetypes: new Map(),
    monsterMemory: new Map(),
    monsterGoals: new Map(),
    orphanedCasters: [],
    summons: [],
    summonBrains: new Map(),
    mode,
    classId: identity.classId,
    level: identity.level,
    xp: identity.xp,
    allocated: identity.allocated,
    stats: identity.stats,
    ability,
    abilities: identity.abilities,
    mana: identity.stats.maxMana,
    manaRegenProgress: 0,
    healthRegenProgress: 0,
    combat,
    projectileEntities: new Map(),
    elapsedTicks: 0,
    kills: 0,
    dyingMonsters: [],
    monsterPathCooldownTicks: 0,
    ...(mode === 'combat-lab'
      ? {
          combatLab: {
            scenario: {
              ...combatLab!.scenario,
              enemy: { ...combatLab!.scenario.enemy },
            },
            spawnTiles,
            spawnCursor: packSize % spawnTiles.length,
          },
        }
      : {}),
    runId,
    backpack: [],
    packs: new Map(),
    monsterPacks: new Map(),
    elites: new Set(),
    rewards: [],
    killsSinceGlobe: 0,
    killsSinceDrop: 0,
    itemRng: arpgItemStream(blueprint.seed),
    drops: 0,
    materialTier: options.materialTier ?? DUNGEON_MATERIAL_TIER,
    ...(population ? { population: population.diagnostics } : {}),
  };

  state.combat.targeting = {
    /**
     * Encounter membership, and it is asked of the caster rather than of the
     * run: a monster's ability points at the Hero and a Hero's points at the
     * monsters. Before monsters could cast, this could answer "the monsters"
     * unconditionally; a monster ability handed that list would swing at its own
     * pack (PRD #793).
     *
     * The dash reads live occupancy along its committed path to decide where it
     * stops and where each shove lands, and it has always been handed every
     * registered monster rather than only the living ones. Every other shape has
     * always been handed the living. Making the two uniform would change what
     * Charge contacts, which is a gameplay decision for a later PRD rather than
     * something to absorb inside a migration, so the split is preserved.
     */
    candidates: ({ world: candidateWorld, caster, ability: definition }) => {
      if (caster.team !== 'player') {
        // A monster's ability answers by team, so a summoned combatant is an
        // ordinary target beside the Hero: hostile to monsters, targetable by
        // them, and shielded by nothing (PRD #804).
        const hero = candidateWorld.get(state.player);
        return [
          ...(hero?.position && !isDead(hero) ? [state.player] : []),
          ...livingDungeonSummons(candidateWorld, state).map(
            (minion) => minion.id,
          ),
        ];
      }
      return definition.shape.kind === 'dash'
        ? [...state.monsters]
        : livingDungeonMonsters(candidateWorld, state).map(
            (monster) => monster.id,
          );
    },
  };
  state.combat.summoning = {
    /**
     * The dungeon's half of the summon payload (PRD #804): a body from the
     * archetype's bestiary identity, painted the summon shadow, registered on
     * the caster's team with the archetype's stats and loadout. Placement is a
     * deterministic search over the cells around the caster, in a documented
     * order, drawing nothing from the rng.
     */
    materialize: ({ caster, payload }) => {
      const owner = world.get(caster.entity);
      const archetype = arpgMonsterArchetype(payload.archetypeId);
      if (!owner?.position || !archetype) return null;
      const at = dungeonSummonTile(world, state, owner.position);
      if (!at) return null;
      const body = spawnDungeonSummonBody(world, at, archetype);
      const stats = archetypeCombatStats(archetype);
      addCombatant(state.combat.combatants, {
        entity: body.id,
        team: caster.team,
        loadout: archetype.loadout,
        stats,
        maxMana: stats.maxMana,
      });
      return body.id;
    },
  };
  for (const [index, candidate] of spawned.entries()) {
    const placement = placements[index]!;
    registerDungeonMonsterCombatant(state, candidate.id, placement.spawn);
    if (placement.spawn.elite) state.elites.add(candidate.id);
    if (placement.packId === undefined) continue;
    state.monsterPacks.set(candidate.id, placement.packId);
    const pack = state.packs.get(placement.packId);
    if (pack) pack.members.push(candidate.id);
    else {
      const planned = population?.packs.find(
        (candidatePack) => candidatePack.id === placement.packId,
      );
      state.packs.set(placement.packId, {
        id: placement.packId,
        template: planned?.template ?? 'melee-swarm',
        members: [candidate.id],
        ...(planned?.guards ? { guards: planned.guards } : {}),
      });
    }
  }
  if (population) installDungeonRewards(world, state, population);
  const campaign =
    mode === 'adventure' && 'campaign' in options ? options.campaign : undefined;
  if (campaign) {
    state.objective = createDungeonObjective(state, population, campaign);
  }
  return state;
}

/**
 * The named guardian packs a procedural floor designates for a slay-guardians
 * objective (PRD #837 slice 2).
 *
 * The packs guarding a cache are already named fights the player must choose,
 * so they stand as the objective's guardians. A floor whose seed planned no
 * cache designates its promoted packs instead: every floor carries at least
 * one elite, so the fallback always names a real fight.
 */
export function designateGuardianPacks(
  population: ArpgPopulationPlan,
): string[] {
  const cacheGuardians = population.packs
    .filter((pack) => pack.guards !== undefined)
    .map((pack) => pack.id);
  if (cacheGuardians.length > 0) return cacheGuardians;
  return population.packs
    .filter((pack) => pack.members.some((member) => member.elite))
    .map((pack) => pack.id);
}

/**
 * Bind one campaign objective to a live run.
 *
 * Every designated guardian pack must actually stand on the floor, which is
 * what makes completion with the objective unmet impossible by construction:
 * an objective can never be born already satisfied or waiting on a pack that
 * never spawned.
 */
function createDungeonObjective(
  state: DungeonCombatState,
  population: ArpgPopulationPlan | null,
  campaign: {
    nodeId: string;
    objective: CampaignObjective;
    guardianPackIds?: readonly string[];
  },
): DungeonObjectiveState {
  const { nodeId, objective } = campaign;
  if (objective.kind === 'slay-boss') {
    return {
      nodeId,
      kind: 'slay-boss',
      bossArchetypeId: objective.bossArchetypeId,
      complete: false,
    };
  }
  const guardianPackIds = [
    ...(campaign.guardianPackIds ??
      (population ? designateGuardianPacks(population) : [])),
  ];
  if (guardianPackIds.length === 0) {
    throw new Error(
      'a slay-guardians objective designates at least one guardian pack',
    );
  }
  for (const packId of guardianPackIds) {
    const pack = state.packs.get(packId);
    if (!pack || pack.members.every((member) => !state.monsters.includes(member))) {
      throw new Error(
        `guardian pack '${packId}' holds no living member on this floor`,
      );
    }
  }
  return { nodeId, kind: 'slay-guardians', guardianPackIds, complete: false };
}

/**
 * Put the floor's designated rewarded dead ends in the world (PRD #794).
 *
 * A guarded cache is a container that exists from the first tick and refuses to
 * open until its named guardian packs are dead, so the detour is a fight rather
 * than a walk. An elite hunt has nothing to place: its payout rides the promoted
 * monster, which is already standing in its pack.
 */
function installDungeonRewards(
  world: World,
  state: DungeonCombatState,
  population: ArpgPopulationPlan,
): void {
  for (const reward of population.rewards) {
    if (reward.kind !== 'guarded-cache') {
      state.rewards.push({
        id: reward.id,
        kind: reward.kind,
        guardianPacks: [],
        unlocked: true,
      });
      continue;
    }
    // A cache is a fight the player chose, so it rolls on the elite side of
    // every window: the detour pays what an elite pays (PRD #794).
    const item = rollDungeonItem(state, {
      table: 'cache',
      itemLevel: DUNGEON_CACHE_ITEM_LEVEL,
      elite: true,
    });
    if (!item) continue;
    const container = world.createEntity({
      position: { ...reward.position, facing: Direction.S },
      dungeonLoot: lootComponent(item),
      interaction: { role: 'usable' },
    });
    state.rewards.push({
      id: reward.id,
      kind: reward.kind,
      container: container.id,
      guardianPacks: [...reward.guardianPacks],
      unlocked: reward.guardianPacks.length === 0,
    });
  }
}

/** Whether one named pack is wholly dead, which is what "cleared" means. */
function packCleared(state: DungeonCombatState, packId: string): boolean {
  const pack = state.packs.get(packId);
  if (!pack) return true;
  return pack.members.every((member) => !state.monsters.includes(member));
}

/** Whether every pack guarding this reward has been cleared. */
function rewardGuardiansCleared(
  state: DungeonCombatState,
  reward: DungeonRewardState,
): boolean {
  return reward.guardianPacks.every((packId) => packCleared(state, packId));
}

/** The archetype one living monster is running, falling back to the adventure's. */
function archetypeFor(
  state: DungeonCombatState,
  monsterId: EntityId,
): ArpgMonsterArchetype {
  const declared = state.monsterArchetypes.get(monsterId);
  return (
    (declared === undefined ? null : arpgMonsterArchetype(declared)) ??
    DUNGEON_ADVENTURE_ARCHETYPE
  );
}

/** The spawn descriptor an authored Combat Lab scenario asks for. */
function scenarioSpawn(scenario: CombatLabScenario): DungeonMonsterSpawn {
  return {
    archetype: scenario.enemy.archetype,
    name: scenario.enemy.name,
    maxHealth: scenario.enemy.maxHealth,
    damage: scenario.enemy.damage,
    msPerTile: scenario.enemy.msPerTile,
  };
}

/**
 * Register one monster as a kernel combatant (PRD #793).
 *
 * A monster and a Hero are now the same record: a team, a loadout of catalog
 * ability ids, a cooldown map, a status list, and derived stats. That is what
 * makes a monster buffable, slowable, resistible, and castable through the
 * machinery that already exists, and it is why the parallel `monsterDamage` and
 * cooldown maps that used to live on run state are gone.
 */
function registerDungeonMonsterCombatant(
  state: DungeonCombatState,
  monsterId: EntityId,
  spawn: DungeonMonsterSpawn,
): void {
  const tuning = spawnTuning(spawn);
  const stats = archetypeCombatStats(tuning.archetype, tuning.level);
  addCombatant(state.combat.combatants, {
    entity: monsterId,
    team: 'monster',
    loadout: tuning.archetype.loadout,
    // The scenario override rides on the derived record rather than beside it,
    // so a lab target that hits for six is a combatant with a six-damage main
    // hand rather than a special case somebody has to remember to read.
    stats: { ...stats, mainHandDamage: tuning.damage, maxHealth: tuning.maxHealth },
    maxMana: stats.maxMana,
  });
  state.monsterArchetypes.set(monsterId, tuning.archetype.id);
  state.monsterMemory.set(monsterId, createBrainMemory());
}

/**
 * Preserve the dungeon's continuous ARPG coordinate across the grid Mobile tick.
 * The Mobile remains the collision anchor, while snapshots expose the exact point.
 */
export function restoreDungeonPlayerPosition(
  world: World,
  state: DungeonCombatState,
): void {
  const player = world.get(state.player);
  if (!player?.position) return;
  const anchor = player.mobile
    ? { x: player.mobile.tileX, y: player.mobile.tileY }
    : state.playerAnchor;
  if (!sameCell(anchor, state.playerAnchor)) {
    state.playerPosition.x += anchor.x - state.playerAnchor.x;
    state.playerPosition.y += anchor.y - state.playerAnchor.y;
    state.playerAnchor = anchor;
  }
  player.position.x = state.playerPosition.x;
  player.position.y = state.playerPosition.y;
  if (player.mobile) player.position.facing = player.mobile.facing;
}

/** Apply one validated dungeon step without discarding its continuous offset. */
export function moveDungeonPlayerAnchor(
  world: World,
  state: DungeonCombatState,
  to: TilePoint,
): void {
  state.playerPosition.x += to.x - state.playerAnchor.x;
  state.playerPosition.y += to.y - state.playerAnchor.y;
  state.playerAnchor = { ...to };
  restoreDungeonPlayerPosition(world, state);
}

/** Adopt one validated, ordered dungeon movement intent. */
export function setDungeonMovementIntent(
  world: World,
  state: DungeonCombatState,
  seq: number,
  intent: DungeonMovementIntent,
  facing: Direction | null = intent.dir,
): void {
  state.movement.seq = seq;
  state.movement.intent = { ...intent };
  state.movement.origin = { ...state.playerPosition };
  state.movement.elapsedMs = 0;
  const player = world.get(state.player);
  if (facing !== null && player?.position) {
    player.mobile?.face(facing);
    player.position.facing = facing;
  }
}

/** Stop continuous locomotion without rewinding its sequence epoch. */
export function stopDungeonMovement(state: DungeonCombatState): void {
  state.movement.intent = { dir: null, running: false };
  state.movement.origin = { ...state.playerPosition };
  state.movement.elapsedMs = 0;
  state.movement.chargeRemainingMs = 0;
  state.movement.prepaidMs = 0;
}

function moveDungeonPlayer(
  world: World,
  state: DungeonCombatState,
  movementMs: number,
): void {
  const player = world.get(state.player);
  if (
    movementMs <= 0 ||
    state.movement.intent.dir === null ||
    !player?.position
  ) {
    restoreDungeonPlayerPosition(world, state);
    return;
  }
  const level = world.map.levelAt(
    state.playerAnchor.x,
    state.playerAnchor.y,
    player.position.z,
  );
  // The player's own read of the movement fold (PRD #804): Wraith Form and a
  // monster's slow scale distance per millisecond, never the millisecond
  // ledger itself, so the seq and elapsedMs the checkpoint protocol validates
  // stay wall time and every replay path through this function scales alike.
  const moved = integrateDungeonMovement(
    world,
    state.playerPosition,
    state.movement.intent,
    movementMs * movementScalarFor(state, state.player),
    level,
  );
  const previousAnchor = state.playerAnchor;
  state.playerPosition = { x: moved.x, y: moved.y };
  state.playerAnchor = cellFor(moved);
  player.mobile?.teleport(state.playerAnchor.x, state.playerAnchor.y);
  if (moved.facing !== undefined) player.mobile?.face(moved.facing);
  state.movement.elapsedMs += movementMs;
  const teleporter =
    !sameCell(previousAnchor, state.playerAnchor)
      ? teleportersByCell(world).get(
          teleporterCellKey(
            state.playerAnchor.x,
            state.playerAnchor.y,
            level,
          ),
        )
      : undefined;
  if (teleporter) {
    player.mobile?.teleport(teleporter.x, teleporter.y);
    state.playerAnchor = { x: teleporter.x, y: teleporter.y };
    state.playerPosition = { x: teleporter.x, y: teleporter.y };
    stopDungeonMovement(state);
    state.movement.repositioned = true;
    player.position.z = world.map.groundedZ(
      teleporter.x,
      teleporter.y,
      teleporter.z,
    );
    restoreDungeonPlayerPosition(world, state);
    return;
  }
  restoreDungeonPlayerPosition(world, state);
  if (level === 0) {
    player.position.z = world.map.landZ(
      state.playerAnchor.x,
      state.playerAnchor.y,
    );
  }
}

/**
 * Advance the authoritative continuous player coordinate for one server step.
 * Charge consumes the start of the step and held input resumes with any time
 * remaining after its lock expires.
 */
export function stepDungeonPlayerMovement(
  world: World,
  state: DungeonCombatState,
  elapsedMs: number,
): void {
  const stepMs = Math.max(0, elapsedMs);
  const blockedMs = Math.max(
    Math.min(stepMs, state.movement.chargeRemainingMs),
    Math.min(stepMs, state.movement.prepaidMs),
  );
  state.movement.chargeRemainingMs = Math.max(
    0,
    state.movement.chargeRemainingMs - stepMs,
  );
  state.movement.prepaidMs = Math.max(
    0,
    state.movement.prepaidMs - stepMs,
  );
  const movementMs = stepMs - blockedMs;
  moveDungeonPlayer(world, state, movementMs);
}

/**
 * Advance through the small fixed-step remainder that precedes an ordered
 * action command. The checkpoint names the current intent and is bounded by
 * elapsed authoritative wall time plus one network/tick tolerance window.
 */
export function synchronizeDungeonMovementForAction(
  world: World,
  state: DungeonCombatState,
  checkpoint: { seq: number; elapsedMs: number },
): boolean {
  if (
    checkpoint.seq !== state.movement.seq ||
    !Number.isFinite(checkpoint.elapsedMs) ||
    checkpoint.elapsedMs < 0
  ) {
    return false;
  }
  if (checkpoint.elapsedMs <= state.movement.elapsedMs) return true;
  const availableLeadMs = Math.max(
    0,
    DUNGEON_ACTION_MOVEMENT_TOLERANCE_MS - state.movement.prepaidMs,
  );
  const maxElapsedMs = state.movement.elapsedMs + availableLeadMs;
  if (checkpoint.elapsedMs > maxElapsedMs) return false;
  const missingMs = checkpoint.elapsedMs - state.movement.elapsedMs;
  if (missingMs <= 0) return true;
  moveDungeonPlayer(world, state, missingMs);
  if (state.movement.repositioned) return false;
  state.movement.prepaidMs += missingMs;
  return true;
}

/**
 * Settle the previous movement intent at an exact, server-validated client edge.
 * Replaying from the intent origin handles both a lagging authority and a fixed
 * tick that briefly ran ahead without quantizing the precise coordinate.
 */
export function settleDungeonMovementIntentCheckpoint(
  world: World,
  state: DungeonCombatState,
  checkpoint: { seq: number; elapsedMs: number },
  maxElapsedMs: number,
): boolean {
  if (
    checkpoint.seq !== state.movement.seq ||
    !Number.isFinite(checkpoint.elapsedMs) ||
    checkpoint.elapsedMs < 0 ||
    checkpoint.elapsedMs > maxElapsedMs
  ) {
    return false;
  }
  const player = world.get(state.player);
  if (!player?.position) return false;
  const origin = { ...state.movement.origin };
  const intent = { ...state.movement.intent };
  state.playerPosition = origin;
  state.playerAnchor = cellFor(origin);
  player.mobile?.teleport(state.playerAnchor.x, state.playerAnchor.y);
  state.movement.elapsedMs = 0;
  state.movement.prepaidMs = 0;
  restoreDungeonPlayerPosition(world, state);

  const replayChunkMs =
    (DUNGEON_PLAYER_COLLISION_SUBDIVISION_TILES /
      DUNGEON_PLAYER_RUN_SPEED_TILES_PER_SECOND) *
    1000;
  let remainingMs = checkpoint.elapsedMs;
  while (remainingMs > 0) {
    const chunkMs = Math.min(remainingMs, replayChunkMs);
    moveDungeonPlayer(world, state, chunkMs);
    if (state.movement.repositioned) return false;
    remainingMs -= chunkMs;
  }
  state.movement.intent = intent;
  state.movement.origin = origin;
  return true;
}

/** Re-anchor an explicit dungeon teleport, including Charge settlement. */
export function placeDungeonPlayer(
  world: World,
  state: DungeonCombatState,
  point: { x: number; y: number },
  options: { preserveIntent?: boolean } = {},
): void {
  const player = world.get(state.player);
  if (!player?.position) return;
  const anchor = cellFor(point);
  player.mobile?.teleport(anchor.x, anchor.y);
  state.playerAnchor = anchor;
  state.playerPosition = { x: point.x, y: point.y };
  if (!options.preserveIntent) stopDungeonMovement(state);
  else {
    state.movement.origin = { ...state.playerPosition };
    state.movement.elapsedMs = 0;
    state.movement.prepaidMs = 0;
  }
  restoreDungeonPlayerPosition(world, state);
}

function groundLootTile(
  world: World,
  origin: { x: number; y: number },
): { x: number; y: number } {
  const occupied = new Set(
    world
      .all()
      .flatMap((entity) => {
        if (!entity.position) return [];
        const cell = cellFor(entity.position);
        return [`${cell.x},${cell.y}`];
      }),
  );
  const offsets = [
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: -1, y: 0 },
    { x: 0, y: -1 },
    { x: 1, y: 1 },
    { x: -1, y: 1 },
    { x: -1, y: -1 },
    { x: 1, y: -1 },
    { x: 0, y: 0 },
  ];
  return (
    offsets
      .map((offset) => ({ x: origin.x + offset.x, y: origin.y + offset.y }))
      .find(
        (tile) =>
          world.map.isWalkable(tile.x, tile.y) &&
          world.map.canStep(origin, tile) &&
          !occupied.has(`${tile.x},${tile.y}`),
      ) ?? origin
  );
}

/**
 * Roll one item from the run's own stream (PRD #795).
 *
 * Everything that varies is a drop position: which table paid, how deep the
 * drop was, and whether an elite paid it. The dungeon's rung on the material
 * ladder and the stream itself belong to the run, and the drop id is the run
 * plus a counter, which is what makes a banked item's identity stable and a
 * reported drop reproducible.
 */
function rollDungeonItem(
  state: DungeonCombatState,
  at: { table: ArpgLootTableId; itemLevel: number; elite: boolean },
): ArpgItem | null {
  const position: ArpgDropPosition = {
    id: `${state.runId}:item:${state.drops}`,
    table: at.table,
    classId: state.classId,
    materialTier: state.materialTier,
    itemLevel: at.itemLevel,
    elite: at.elite,
  };
  state.drops += 1;
  return rollArpgItem(state.itemRng, position);
}

/** The ground entity's payload for one rolled item. */
function lootComponent(item: ArpgItem): {
  item: ArpgItem;
  name: string;
  rarity: ArpgItem['rarity'];
  artId: number;
} {
  return {
    item,
    name: arpgItemName(item),
    rarity: item.rarity,
    artId: arpgItemBaseOf(item)?.artId ?? 0,
  };
}

/**
 * Settle what one kill pays: experience, sustain, and a drop (PRD #794).
 *
 * Every rule here is deterministic and countable, because a dense floor is only
 * survivable if the player can rely on what killing things gives back. Ordinary
 * kills pay a globe on a fixed interval and an item on a sparser one; an elite
 * pays both, guaranteed. What the item rolls is the archetype's loot table and
 * the run's item stream (PRD #795), so a caster pays in books where a skeleton
 * pays in steel.
 */
function rewardDungeonKill(
  world: World,
  state: DungeonCombatState,
  monsterPosition: { x: number; y: number },
  xpValue: number,
  elite: boolean,
  archetype: ArpgMonsterArchetype,
): void {
  // What a kill is worth is declared by the archetype rather than by a constant
  // here, so a tougher monster paying more is content work (PRD #793).
  state.xp += elite ? xpValue * ARPG_ELITE_XP_MULTIPLIER : xpValue;

  state.killsSinceGlobe += 1;
  if (elite || state.killsSinceGlobe >= DUNGEON_HEALTH_GLOBE_KILL_INTERVAL) {
    state.killsSinceGlobe = 0;
    spawnDungeonHealthGlobe(world, state, monsterPosition);
  }

  state.killsSinceDrop += 1;
  if (!elite && state.killsSinceDrop < DUNGEON_ITEM_DROP_KILL_INTERVAL) return;
  state.killsSinceDrop = 0;
  const item = rollDungeonItem(state, {
    table: archetype.lootTable,
    // A monster's own level is the drop's depth, so a tougher archetype pays a
    // deeper roll without a second table of level numbers to keep in step.
    itemLevel: archetype.base.level,
    elite,
  });
  if (!item) return;
  const position = groundLootTile(world, monsterPosition);
  world.createEntity({
    position: { ...position, facing: Direction.S },
    dungeonLoot: lootComponent(item),
    interaction: { role: 'usable' },
  });
}

/**
 * Drop one contact-collected health globe.
 *
 * Sustain with no inventory interaction: the globe is a ground entity the player
 * walks over, which is the whole of the interface (user story fourteen).
 */
function spawnDungeonHealthGlobe(
  world: World,
  state: DungeonCombatState,
  at: { x: number; y: number },
): void {
  const position = groundLootTile(world, at);
  world.createEntity({
    position: { ...position, facing: Direction.S },
    dungeonGlobe: {
      heal: Math.max(
        1,
        Math.round(state.stats.maxHealth * DUNGEON_HEALTH_GLOBE_SHARE),
      ),
    },
    interaction: { role: 'scenery' },
  });
}

/**
 * Collect every globe the player is standing on.
 *
 * Contact rather than a command, so sustain never competes with an ability for
 * the player's hands. A globe over-healing is simply capped.
 */
function collectDungeonHealthGlobes(
  world: World,
  state: DungeonCombatState,
): void {
  const player = world.get(state.player);
  const health = player?.health;
  if (!player?.position || !health || isDead(player)) return;
  for (const entity of world.all()) {
    const globe = entity.dungeonGlobe;
    if (!globe || !entity.position) continue;
    const distance = Math.hypot(
      entity.position.x - player.position.x,
      entity.position.y - player.position.y,
    );
    if (distance > DUNGEON_HEALTH_GLOBE_PICKUP_TILES) continue;
    world.remove(entity.id);
    if (health.current >= health.max) continue;
    health.current = Math.min(health.max, health.current + globe.heal);
  }
}

export type DungeonLootPickupResult =
  | { ok: true; item: ArpgItem }
  | { ok: false; reason: 'actor' | 'missing' | 'range' | 'duplicate' | 'locked' };

export function pickupDungeonLoot(
  world: World,
  state: DungeonCombatState,
  actorId: EntityId,
  lootId: EntityId,
): DungeonLootPickupResult {
  if (actorId !== state.player) return { ok: false, reason: 'actor' };
  const actor = world.get(actorId);
  const loot = world.get(lootId);
  if (!actor?.position || !loot?.position || !loot.dungeonLoot) {
    return { ok: false, reason: 'missing' };
  }
  const distance = Math.max(
    Math.abs(actor.position.x - loot.position.x),
    Math.abs(actor.position.y - loot.position.y),
  );
  if (distance > 2 || (actor.position.z ?? 0) !== (loot.position.z ?? 0)) {
    return { ok: false, reason: 'range' };
  }
  // A designated cache is guarded: it is in the world from the first tick and
  // opens only once the packs that guard it are dead (PRD #794).
  const guarded = state.rewards.find((reward) => reward.container === lootId);
  if (guarded && !guarded.unlocked && !rewardGuardiansCleared(state, guarded)) {
    return { ok: false, reason: 'locked' };
  }
  const item = { ...loot.dungeonLoot.item };
  if (state.backpack.some((candidate) => candidate.id === item.id)) {
    return { ok: false, reason: 'duplicate' };
  }
  state.backpack.push(item);
  world.remove(lootId);
  return { ok: true, item };
}

/**
 * What one ability's damage payload deals from these inputs.
 *
 * The arithmetic is the kernel's: this reads the catalog's `damage` payload and
 * hands it to the same scaling function resolution uses, so a tuning change on
 * the catalog entry moves the number here too. An ability with no damage payload,
 * such as Aegis, deals nothing.
 *
 * An optional derived record replaces the level and weapon inputs, which is how
 * a caller asks what an allocated Hero deals rather than what a bare one does.
 * Given neither, the answer is the un-allocated number, unchanged.
 */
export function dungeonAbilityDamage(
  level: number,
  mainHandDamage: number = DUNGEON_WARRIOR_LOADOUT.mainHand.damage,
  abilityId: AbilityId = DUNGEON_WARRIOR_LOADOUT.ability,
  stats?: CombatStats,
): number {
  const payload = catalogAbility(abilityId)?.payloads.find(
    (entry) => entry.kind === 'damage',
  );
  if (payload?.kind !== 'damage') return 0;
  return scaledDamage(
    stats ??
      deriveCombatStats({
        level,
        mainHand: { damage: mainHandDamage, grip: 'one-handed' },
      }),
    payload,
  );
}

function livingDungeonMonsters(
  world: World,
  state: DungeonCombatState,
) {
  return state.monsters.flatMap((id) => {
    const entity = world.get(id);
    return entity?.position && entity.health && !isDead(entity) ? [entity] : [];
  });
}

function nextCombatLabSpawn(
  world: World,
  state: DungeonCombatState,
): TilePoint {
  const lab = state.combatLab;
  if (!lab) throw new Error('combat lab state is missing its spawn plan');
  const occupied = new Set(
    [
      state.player,
      ...state.monsters,
      ...state.dyingMonsters.map(({ id }) => id),
    ].flatMap((id) => {
      const position = world.get(id)?.position;
      if (!position) return [];
      const cell = cellFor(position);
      return [`${cell.x},${cell.y}`];
    }),
  );
  for (let offset = 0; offset < lab.spawnTiles.length; offset++) {
    const index = (lab.spawnCursor + offset) % lab.spawnTiles.length;
    const spawn = lab.spawnTiles[index]!;
    if (occupied.has(`${spawn.x},${spawn.y}`)) continue;
    lab.spawnCursor = (index + 1) % lab.spawnTiles.length;
    return spawn;
  }
  const fallback = lab.spawnTiles[lab.spawnCursor]!;
  lab.spawnCursor = (lab.spawnCursor + 1) % lab.spawnTiles.length;
  return fallback;
}

function recordDungeonMonsterDeath(
  world: World,
  state: DungeonCombatState,
  monsterId: EntityId,
  effects: DungeonCombatEffect[],
): void {
  const position = world.get(monsterId)?.position;
  const archetype = archetypeFor(state, monsterId);
  // The declared archetype id, read before the maps are cleaned: the slay-boss
  // objective keys on it, and `archetypeFor` silently falls back.
  const declaredArchetype = state.monsterArchetypes.get(monsterId);
  state.monsters = state.monsters.filter((id) => id !== monsterId);
  state.monsterRoutes.delete(monsterId);
  state.monsterGoals.delete(monsterId);
  state.monsterMemory.delete(monsterId);
  state.monsterArchetypes.delete(monsterId);
  // Entity ids are recycled, so the cooldowns, statuses, and casts a dead
  // monster owned go with it rather than waiting for whoever inherits its id.
  // The one exception is a cast the ability said should outlive its caster: a
  // hazard the player has already been told about lands whether or not the
  // thing that announced it survives, and the kernel can only keep that promise
  // while the combatant is still registered.
  const pending = castFor(state.combat, monsterId);
  if (pending && catalogAbility(pending.ability)?.casterDeath === 'persist') {
    state.orphanedCasters.push(monsterId);
  } else {
    removeCombatant(state.combat, monsterId);
  }
  state.dyingMonsters.push({ id: monsterId, remainingTicks: 20 });
  state.kills += 1;
  effects.push({ kind: 'monster-die', monster: monsterId });

  if (state.mode === 'combat-lab' && state.combatLab?.scenario.respawns) {
    const spawn = scenarioSpawn(state.combatLab.scenario);
    const replacement = spawnDungeonMonster(
      world,
      nextCombatLabSpawn(world, state),
      spawn,
    );
    state.monsters.push(replacement.id);
    state.monsterRoutes.set(replacement.id, []);
    registerDungeonMonsterCombatant(state, replacement.id, spawn);
    state.monster = state.monsters[0]!;
    return;
  }
  const elite = state.elites.delete(monsterId);
  if (position) {
    rewardDungeonKill(world, state, position, archetype.xpValue, elite, archetype);
  }
  // A cache opens the moment its last guardian falls, and never before, which is
  // what makes a guarded cache a fight rather than a container (PRD #794).
  for (const reward of state.rewards) {
    if (reward.unlocked || !rewardGuardiansCleared(state, reward)) continue;
    reward.unlocked = true;
  }
  settleObjectiveProgress(state, declaredArchetype, effects);
}

/**
 * Advance the run's campaign objective on one monster death (PRD #837 slice 2).
 *
 * Completion is settled here exactly once, at the kill that satisfied it, and
 * announced as a semantic effect so the server can settle the node durably on
 * the Soulstone. The `complete` latch is the in-run idempotence: however many
 * deaths follow, the objective never re-announces.
 */
function settleObjectiveProgress(
  state: DungeonCombatState,
  deadArchetype: ArpgMonsterArchetypeId | undefined,
  effects: DungeonCombatEffect[],
): void {
  const objective = state.objective;
  if (!objective || objective.complete) return;
  const satisfied =
    objective.kind === 'slay-guardians'
      ? objective.guardianPackIds.every((packId) => packCleared(state, packId))
      : deadArchetype === objective.bossArchetypeId;
  if (!satisfied) return;
  objective.complete = true;
  effects.push({
    kind: 'objective-complete',
    nodeId: objective.nodeId,
    objective: objective.kind,
  });
}

/**
 * Release a dead caster once the hazard it committed to has landed.
 *
 * Held open only as long as the cast is: the tick the kernel finishes with it,
 * the combatant goes, because an entity id that outlives its owner is an entity
 * id somebody else inherits along with its cooldowns.
 */
function releaseOrphanedCasters(state: DungeonCombatState): void {
  state.orphanedCasters = state.orphanedCasters.filter((id) => {
    if (castFor(state.combat, id)) return true;
    removeCombatant(state.combat, id);
    return false;
  });
}

/**
 * An ability's presentation descriptor with its grip-conditional clip already
 * resolved for the caster who cast it.
 *
 * The grip is a property of the caster, not of the ability, so resolving it
 * here means no reader downstream has to know that grip-conditional clips
 * exist. Everything else on the descriptor travels as authored.
 */
export interface DungeonAbilityPresentation {
  /** The player-facing ability name. */
  name: string;
  /** The clip this caster plays, absent when the ability animates nothing. */
  clip?: string;
  /** Authored effect ids the client may key visuals off. */
  effectIds: readonly string[];
}

/**
 * Resolve one ability's presentation descriptor for this caster.
 *
 * Presentation is read here and nowhere else in this module: this function is
 * the effect-stream edge, which is exactly the layer allowed to translate a
 * descriptor. Resolution never reaches it.
 */
function heroAbilityPresentation(
  abilityId: AbilityId,
  grip: WeaponGrip,
): DungeonAbilityPresentation {
  const descriptor = abilityPresentation(abilityId);
  if (!descriptor) return { name: abilityId, effectIds: [] };
  const clip = descriptor.clip;
  const clipId = !clip
    ? undefined
    : clip.kind === 'grip'
      ? grip === 'two-handed'
        ? clip.twoHanded
        : clip.oneHanded
      : clip.clipId;
  return {
    name: descriptor.name,
    ...(clipId !== undefined ? { clip: clipId } : {}),
    effectIds: [...descriptor.effectIds],
  };
}

/**
 * Re-anchor the dungeon's continuous player coordinate after a dash.
 *
 * The kernel already moved the body, its tile anchor, and its level. What it
 * cannot know is that this caller keeps a second, authoritative copy of the
 * player's exact position for movement prediction, so the adapter settles that
 * copy and starts the locomotion lock the dash owns.
 */
function settleDungeonCharge(
  world: World,
  state: DungeonCombatState,
  durationMs: number,
  dash: NonNullable<FrozenGeometry['dash']>,
): void {
  placeDungeonPlayer(world, state, dash.end, { preserveIntent: true });
  state.movement.chargeRemainingMs = durationMs;
  for (const contact of dash.contacts) {
    // A shoved body is no longer on the route it was walking.
    if (contact.to) state.monsterRoutes.set(contact.target, []);
  }
}

/** The facing an event resolved to, falling back to where the caster looks. */
function castFacing(
  world: World,
  caster: EntityId,
  geometry: FrozenGeometry,
): Direction {
  return (
    geometry.facing ?? world.get(caster)?.position?.facing ?? Direction.S
  );
}

/**
 * Translate one resolution into the dungeon's effect stream.
 *
 * There is one effect for every ability, carrying the ability id, the frozen
 * geometry the cast committed to, the per-target hits, and the presentation
 * descriptor. Nothing here branches on which ability was cast, so a sixth
 * ability travels on the wire and renders on the client with no edit to either.
 *
 * Everything an effect carries is already applied: the kernel dealt the damage,
 * applied the status, and moved the shoved bodies before it emitted the event.
 */
function translateResolution(
  world: World,
  state: DungeonCombatState,
  event: Extract<CombatEvent, { kind: 'ability-resolved' }>,
  statusDurationTicks: number,
): DungeonCombatEffect[] {
  const shape = event.geometry.shape;
  const dash = event.geometry.dash;
  // The dash is the one shape that moved its caster, so the dungeon's second
  // copy of the player position is settled before the effect goes out. Only the
  // player has that second copy; a dashing monster is where the kernel left it.
  if (shape.kind === 'dash' && dash && event.caster === state.player) {
    settleDungeonCharge(world, state, shape.durationMs, dash);
  }
  return [
    {
      kind: 'ability-cast',
      ability: event.ability,
      caster: event.caster,
      facing: castFacing(world, event.caster, event.geometry),
      presentation: heroAbilityPresentation(event.ability, state.stats.mainHandGrip),
      shape: shape.kind,
      hits: event.hits.map((hit) => ({
        target: hit.target,
        damage: hit.damage,
        dead: hit.dead,
      })),
      ...(shape.kind === 'dash'
        ? {
            dash: {
              path: dash ? dash.path.map((tile) => ({ ...tile })) : [],
              contacts: dash
                ? dash.contacts.map((contact) => ({ ...contact }))
                : [],
              durationMs: shape.durationMs,
            },
          }
        : {}),
      ...(statusDurationTicks > 0 ? { statusDurationTicks } : {}),
    },
  ];
}

/**
 * Translate one telegraph into the dungeon's effect stream.
 *
 * Nothing the Warrior carries winds up, so this channel is empty today. It
 * exists so a telegraphed monster ability has somewhere to announce itself
 * without a protocol change, which is the whole point of the generic effect.
 */
function translateWindUp(
  world: World,
  state: DungeonCombatState,
  event: Extract<CombatEvent, { kind: 'cast-started' }>,
  areas: readonly Extract<CombatEvent, { kind: 'area-telegraphed' }>[],
): DungeonCombatEffect[] {
  const casterPosition = world.get(event.caster)?.position;
  const fallbackShape = catalogAbility(event.ability)?.shape ?? { kind: 'self' as const };
  if (areas.length === 0) {
    // A wind-up with no announced area: a drawn bow. The pose is the whole
    // read, so there is nothing to draw on the ground.
    if (!casterPosition) return [];
    return [
      {
        kind: 'ability-telegraph',
        ability: event.ability,
        caster: event.caster,
        facing: casterPosition.facing ?? Direction.S,
        presentation: heroAbilityPresentation(
          event.ability,
          state.stats.mainHandGrip,
        ),
        origin: { x: casterPosition.x, y: casterPosition.y },
        leadTicks: event.castTicks,
        geometry: { ...fallbackShape },
      },
    ];
  }
  // One effect per announced footprint, which is one for an ordinary telegraph
  // and one per impact for a pattern. Every one of them is on the wire before
  // the first lands, because an area the player cannot see coming cannot be
  // answered (PRD #793).
  return areas.map((area) => ({
    kind: 'ability-telegraph',
    ability: event.ability,
    caster: event.caster,
    facing: castFacing(world, event.caster, area.geometry),
    presentation: heroAbilityPresentation(event.ability, state.stats.mainHandGrip),
    origin: { x: area.geometry.origin.x, y: area.geometry.origin.y },
    ...(area.geometry.point ? { point: { ...area.geometry.point } } : {}),
    leadTicks: area.leadTicks,
    geometry: { ...area.geometry.shape },
  }));
}

/**
 * Turn the kernel's semantic events into the dungeon's effects.
 *
 * Death is reported by the kernel and enacted here, because what a downed
 * monster looks like - the dying window, the kill count, the loot roll, the
 * combat lab respawn - is entirely the caller's business.
 */
function translateCastEvents(
  world: World,
  state: DungeonCombatState,
  events: readonly CombatEvent[],
): DungeonCombatEffect[] {
  const effects: DungeonCombatEffect[] = [];
  const deaths: EntityId[] = [];
  let statusDurationTicks = 0;
  const areas = new Map<
    EntityId,
    Extract<CombatEvent, { kind: 'area-telegraphed' }>[]
  >();
  for (const event of events) {
    if (event.kind === 'status-applied') {
      statusDurationTicks = event.durationTicks;
    } else if (event.kind === 'area-telegraphed') {
      // The announced areas belong to the wind-up that started in the same
      // batch, so they are carried onto that one cast's announcement rather
      // than sent as separate announcements of the same cast. A pattern
      // announces several footprints from one wind-up.
      const announced = areas.get(event.caster) ?? [];
      announced.push(event);
      areas.set(event.caster, announced);
    }
  }
  for (const event of events) {
    if (event.kind === 'cast-started') {
      // Every wind-up announces itself, whether or not it announces an area.
      // A telegraphed nova has an area to draw and a drawn bow has only the
      // pose, but both owe the player the same read: something is coming and
      // there is time to move (PRD #800).
      if (event.castTicks > 0) {
        effects.push(
          ...translateWindUp(world, state, event, areas.get(event.caster) ?? []),
        );
      }
    } else if (event.kind === 'ability-resolved') {
      for (const hit of event.hits) {
        if (hit.damage <= 0) continue;
        alertDamagedMonster(world, state, event.caster, hit.target);
        followDungeonTarget(state, event.caster, hit.target);
      }
      effects.push(
        ...translateResolution(world, state, event, statusDurationTicks),
      );
    } else if (event.kind === 'projectile-launched') {
      spawnDungeonProjectileBody(world, state, event);
    } else if (event.kind === 'projectile-hit') {
      if (event.hit.damage > 0) {
        alertDamagedMonster(world, state, event.caster, event.hit.target);
        followDungeonTarget(state, event.caster, event.hit.target);
      }
      const body = state.projectileEntities.get(event.projectile);
      effects.push({
        kind: 'projectile-hit',
        projectile: body ?? event.caster,
        target: event.hit.target,
        damage: event.hit.damage,
        dead: event.hit.dead,
      });
    } else if (event.kind === 'projectile-ended') {
      const body = state.projectileEntities.get(event.projectile);
      if (body !== undefined) {
        world.remove(body);
        state.projectileEntities.delete(event.projectile);
      }
    } else if (event.kind === 'combatant-summoned') {
      // The kernel registered the combatant and stamped the bond; the run
      // adopts it into membership, its declared brain, and steering (PRD #804).
      adoptDungeonSummon(state, event.summon, event.brainId as BrainId);
    } else if (event.kind === 'summon-ended') {
      // Expired, replaced, or dissipated with its owner: the kernel already
      // released the combatant, and the body crumbles here.
      releaseDungeonSummon(world, state, event.summon, effects);
    } else if (event.kind === 'combatant-died') {
      deaths.push(event.combatant);
    }
  }
  for (const casualty of deaths) {
    // A shot can strike a body the same tick a cast killed it, and the kernel
    // reports the death once per killing blow. Settling twice would double the
    // kill count and the loot.
    if (state.monsters.includes(casualty)) {
      recordDungeonMonsterDeath(world, state, casualty, effects);
    } else if (state.summons.includes(casualty)) {
      // A summon killed by damage dies like a monster on the screen and like
      // nothing at all in the ledgers: no XP, no loot, no kill count.
      releaseDungeonSummon(world, state, casualty, effects);
    } else if (casualty === state.player) {
      // The kernel reports a death and never enacts one, because what a downed
      // body looks like belongs to the caller. A monster's swing kills the Hero
      // through a cast now, so this is where that lands (PRD #793).
      const player = world.get(state.player);
      if (player && !player.death) enactDeath(player);
    }
  }
  return effects;
}

/**
 * Point the run's single-target interface at whatever the player just hit.
 *
 * `state.monster` predates the population: it was the one monster a floor held.
 * It survives because the HUD reads one target frame, and on a populated floor
 * the honest answer to "which one" is the last one the player actually swung
 * at (PRD #794).
 */
function followDungeonTarget(
  state: DungeonCombatState,
  attacker: EntityId,
  target: EntityId,
): void {
  if (attacker !== state.player || !state.monsters.includes(target)) return;
  state.monster = target;
}

/**
 * Wake a monster that just took a hit, whether or not it could see who threw it.
 *
 * This is the damage-activation half of detection: proximity and sight decide
 * what a monster notices on its own, and being hit decides the rest. Opening
 * from outside its detection radius still starts a fight, which is the whole
 * point of opening from there.
 */
function alertDamagedMonster(
  world: World,
  state: DungeonCombatState,
  attacker: EntityId,
  target: EntityId,
): void {
  const memory = state.monsterMemory.get(target);
  const attackerPosition = world.get(attacker)?.position;
  if (!memory || !attackerPosition || attacker === target) return;
  state.monsterMemory.set(
    target,
    activateMemory(memory, attacker, {
      x: attackerPosition.x,
      y: attackerPosition.y,
    }),
  );
}

/**
 * Give one shot in flight a body on the wire.
 *
 * The presentation descriptor names the art, which is why this lives at the
 * effect-stream edge beside {@link heroAbilityPresentation} and not in the
 * kernel: the kernel put an object in the air and has no idea whether it is an
 * arrow, a bolt, or a bead of fire. The body carries no damage of its own,
 * because the shot's payloads are the kernel's to apply, and nothing steps it:
 * {@link syncDungeonProjectileBodies} moves it to wherever the kernel says the
 * shot now is.
 */
function spawnDungeonProjectileBody(
  world: World,
  state: DungeonCombatState,
  event: Extract<CombatEvent, { kind: 'projectile-launched' }>,
): void {
  const look = abilityPresentation(event.ability)?.projectile;
  if (!look) return;
  const body = spawnProjectile(
    world,
    {
      x: event.origin.x,
      y: event.origin.y,
      ...(event.origin.z === undefined ? {} : { z: event.origin.z }),
    },
    { x: event.velocity.x, y: event.velocity.y },
    {
      ownerId: event.caster,
      damage: 0,
      speedTilesPerS: event.speedTilesPerS,
      ttlTicks: event.ttlTicks,
      look: look as ProjectileEffectId,
    },
  );
  state.projectileEntities.set(event.projectile, body.id);
}

/**
 * Move each shot's body to where the kernel says its shot is, and drop a body
 * whose shot is gone.
 *
 * One authority over flight: the kernel advances the shot, this copies the
 * result onto the entity the client draws, so the arrow a player dodges is the
 * arrow the server simulated.
 */
function syncDungeonProjectileBodies(
  world: World,
  state: DungeonCombatState,
): void {
  const live = new Set<ProjectileHandle>();
  for (const projectile of activeProjectiles(state.combat)) {
    live.add(projectile.id);
    const body = state.projectileEntities.get(projectile.id);
    const position = body === undefined ? undefined : world.get(body)?.position;
    if (!position) continue;
    position.x = projectile.position.x;
    position.y = projectile.position.y;
    if (projectile.position.z !== undefined) position.z = projectile.position.z;
  }
  for (const [handle, body] of state.projectileEntities) {
    if (live.has(handle)) continue;
    world.remove(body);
    state.projectileEntities.delete(handle);
  }
}

/**
 * The kernel refuses a cast with one more reason than the dungeon has ever
 * needed. `busy` is unreachable for the shipped loadout, because every Warrior
 * ability is instant with no recovery and therefore never leaves a cast in
 * flight, so it is folded onto `ability` rather than widening the wire union
 * with a state no client can observe.
 */
function dungeonRejection(reason: CastRejection): DungeonCastRejection {
  return reason === 'busy' ? 'ability' : reason;
}

/**
 * Cast one Warrior ability.
 *
 * A thin adapter: sync the run's mana and derived stats onto the player
 * combatant, ask the kernel for the cast, translate what came back. Every
 * mechanical decision - who is in range, how much damage, what the cooldown is,
 * whether the aim is legal - belongs to the kernel.
 */
export function castDungeonAbility(
  world: World,
  state: DungeonCombatState,
  aim: { x: number; y: number },
  abilityId: AbilityId = state.ability,
): DungeonCastResult {
  const caster = combatantFor(state.combat.combatants, state.player);
  if (!caster) return { accepted: false, reason: 'ability' };
  // Mana and the derived stats are dungeon run state. The kernel spends the
  // one and reads the other, so they are handed over immediately before the
  // cast and the remainder is read straight back.
  caster.resources.mana = state.mana;
  caster.stats = state.stats;
  const result = requestCast(world, state.combat, state.player, abilityId, aim);
  if (!result.accepted) {
    return { accepted: false, reason: dungeonRejection(result.reason) };
  }
  state.mana = caster.resources.mana;
  return {
    accepted: true,
    effects: translateCastEvents(world, state, result.events),
  };
}

export type DungeonCombatEffect =
  | {
      kind: 'projectile-hit';
      projectile: EntityId;
      target: EntityId;
      damage: number;
      dead: boolean;
    }
  | { kind: 'monster-die'; monster: EntityId }
  | {
      /**
       * The run's campaign objective was just satisfied (PRD #837 slice 2).
       *
       * A semantic event, emitted exactly once per run at the kill that
       * settled it. The server settles the node durably on the Soulstone when
       * it sees this; the exit needs no notice because the exit was never
       * gated.
       */
      kind: 'objective-complete';
      nodeId: string;
      objective: CampaignObjectiveKind;
    }
  | {
      /**
       * One resolved ability cast, whichever ability it was.
       *
       * This is the generic cast effect PRD #784 asks for: the ability id, the
       * frozen geometry the cast committed to, the per-target hits, and the
       * presentation descriptor. Adding an ability adds no member here.
       */
      kind: 'ability-cast';
      ability: AbilityId;
      caster: EntityId;
      /** The facing the frozen aim resolved to. */
      facing: Direction;
      /** Clips, effect ids, and name, already resolved for the caster's grip. */
      presentation: DungeonAbilityPresentation;
      /** The delivery shape that selected the hits. Mechanical, never a clip. */
      shape: AbilityShape['kind'];
      hits: {
        target: EntityId;
        damage: number;
        dead: boolean;
      }[];
      /** Frozen traversal, present only for a dash shape. */
      dash?: {
        path: TilePoint[];
        contacts: DungeonChargeContact[];
        durationMs: number;
      };
      /** How long the status this cast applied runs, when it applied one. */
      statusDurationTicks?: number;
    }
  | {
      /**
       * One announced area, emitted before the damage it announces.
       *
       * Nothing the Warrior carries winds up, so nothing emits this today. It
       * exists so telegraphed monster abilities have a channel that already
       * works when they arrive.
       */
      kind: 'ability-telegraph';
      ability: AbilityId;
      caster: EntityId;
      facing: Direction;
      presentation: DungeonAbilityPresentation;
      /** Where the frozen geometry is anchored. */
      origin: { x: number; y: number };
      /** The frozen aim point, for shapes that aim at one. */
      point?: { x: number; y: number };
      /** Ticks of lead before the area resolves. */
      leadTicks: number;
      /**
       * The announced footprint, by value: the delivery shape the frozen
       * geometry committed to, so a client can draw the exact area that will be
       * damaged rather than a marker on one tile (PRD #793).
       */
      geometry: AbilityShape;
    };

/**
 * What a combatant's statuses do to how far it walks in a tick.
 *
 * This is the read site the `moveSpeedPercent` modifier never had. Every other
 * folded modifier is read where its effect happens - damage taken and damage
 * dealt at the kernel's damage sites, resource regeneration in the fold - and
 * movement happens here, in the adapter that owns locomotion, so a Snare Trap
 * slows what it caught rather than deriving a number nobody reads (PRD #800).
 *
 * Nothing here knows a status called `slow` exists: it reads one folded record,
 * like every other consumer. The floor keeps a stacked debuff from stopping a
 * body outright, which would be a stun rather than a slow.
 */
const DUNGEON_MIN_MOVE_SPEED_SCALAR = 0.2;

function movementScalarFor(
  state: DungeonCombatState,
  entity: EntityId,
): number {
  const combatant = combatantFor(state.combat.combatants, entity);
  if (!combatant || combatant.statuses.length === 0) return 1;
  const { moveSpeedPercent } = effectiveModifiers(combatant);
  if (!Number.isFinite(moveSpeedPercent) || moveSpeedPercent === 0) return 1;
  return Math.max(DUNGEON_MIN_MOVE_SPEED_SCALAR, 1 + moveSpeedPercent / 100);
}

/**
 * One tick of Hero regeneration, mana and health alike.
 *
 * Both rates come from the derived record, so Wisdom moves the one and Vitality
 * the other, and a class curve declares the baseline. The fractional progress
 * is carried between ticks rather than rounded away, which is what lets a rate
 * far below one point per tick still land: the shipped Warrior regenerates one
 * mana every five ticks, and that is now a rate of `0.2` rather than a counter
 * comparing against a constant.
 *
 * Health regeneration is zero for a Warrior who has spent nothing, which is why
 * the dungeon's `regenPaused` health component is still correct: the pause
 * stops the platform's own regeneration, and this is the Hero's.
 */
function regenerateDungeonVitals(world: World, state: DungeonCombatState): void {
  const { maxMana, manaRegenPerTick, healthRegenPerTick } = state.stats;
  if (state.mana < maxMana && manaRegenPerTick > 0) {
    state.manaRegenProgress += manaRegenPerTick;
    const points = Math.floor(state.manaRegenProgress);
    if (points > 0) {
      state.mana = Math.min(maxMana, state.mana + points);
      state.manaRegenProgress -= points;
    }
  } else if (state.mana >= maxMana) {
    state.manaRegenProgress = 0;
  }

  const player = world.get(state.player);
  const health = player?.health;
  if (!health || isDead(player!) || healthRegenPerTick <= 0) {
    if (healthRegenPerTick <= 0) state.healthRegenProgress = 0;
    return;
  }
  if (health.current >= health.max) {
    state.healthRegenProgress = 0;
    return;
  }
  state.healthRegenProgress += healthRegenPerTick;
  const points = Math.floor(state.healthRegenProgress);
  if (points <= 0) return;
  state.healthRegenProgress -= points;
  health.current = Math.min(health.max, health.current + points);
}

/**
 * How far an ability can select or fly, in tiles.
 *
 * A brain uses this to ask "is my target in range of this", so it never has to
 * know which shapes exist. A `tile` ability is placed at an aimed point rather
 * than measured from the caster and therefore has no inherent reach at all;
 * answering infinity is honest about that, and the brain's own band is what
 * bounds where it places one.
 */
function abilityReachTiles(shape: AbilityShape): number {
  switch (shape.kind) {
    case 'self':
      return 0;
    case 'cone':
    case 'line':
    case 'projectile':
      return shape.rangeTiles;
    case 'circle':
      return shape.radiusTiles;
    case 'dash':
      return shape.distanceTiles;
    case 'tile':
      return Number.POSITIVE_INFINITY;
    default: {
      const exhaustive: never = shape;
      void exhaustive;
      return 0;
    }
  }
}

/**
 * Everything one monster's brain is allowed to know this tick.
 *
 * The snapshot is built here, in the adapter that owns the world, so that a
 * brain is a pure function of value records: it reads distances, sight, reach,
 * and readiness, and it cannot reach an entity, a map, or a cooldown map even
 * by accident.
 */
function perceivedAbilitiesFor(
  state: DungeonCombatState,
  combatantId: EntityId,
): PerceivedAbility[] {
  const combatant = combatantFor(state.combat.combatants, combatantId);
  return (combatant?.loadout ?? []).flatMap((abilityId) => {
    const definition = catalogAbility(abilityId);
    if (!definition || !combatant) return [];
    return [
      {
        id: abilityId,
        ready:
          cooldownRemaining(combatant, abilityId) === 0 &&
          combatant.resources.mana >= definition.costs.mana,
        counterplay: definition.counterplay,
        shapeKind: definition.shape.kind,
        rangeTiles: abilityReachTiles(definition.shape),
      },
    ];
  });
}

/**
 * One combatant as another perceives it: distance, sight, and the walkability
 * answer that keeps a swing from landing through the corner of a wall.
 */
function perceivedTargetFor(
  world: World,
  target: Entity,
  observer: {
    point: BrainPoint;
    cell: TilePoint;
    level: number;
    sight: (from: BrainPoint, to: BrainPoint) => boolean;
  },
): PerceivedTarget | null {
  const position = target.position;
  if (!position) return null;
  const point = { x: position.x, y: position.y };
  const cell = dungeonCellFor(position);
  const level = world.map.levelAt(cell.x, cell.y, position.z);
  const sameLevel = level === observer.level;
  return {
    id: target.id,
    position: point,
    distanceTiles: Math.hypot(
      observer.point.x - point.x,
      observer.point.y - point.y,
    ),
    // Sight is the mask the movement layer walks, so cover a player can see
    // is cover a player can use.
    visible: sameLevel && observer.sight(observer.point, point),
    reachable:
      sameLevel &&
      (sameCell(observer.cell, cell) ||
        canStepForLevel(world, observer.level)(observer.cell, cell)),
  };
}

function perceiveDungeonMonster(
  world: World,
  state: DungeonCombatState,
  monster: Entity,
  context: {
    candidateLevel: number;
    player: Entity;
    livingMonsters: readonly Entity[];
    livingSummons: readonly Entity[];
    sight: (from: BrainPoint, to: BrainPoint) => boolean;
  },
): BrainPerception {
  const position = monster.position!;
  const observer = {
    point: { x: position.x, y: position.y },
    cell: dungeonCellFor(position),
    level: context.candidateLevel,
    sight: context.sight,
  };
  // The Hero first, then every living summon: a monster brain treats a
  // summoned combatant as an ordinary target, and the shared acquisition rule
  // picks whichever is nearest (PRD #804).
  const targets = [context.player, ...context.livingSummons].flatMap(
    (candidate) => {
      const target = perceivedTargetFor(world, candidate, observer);
      return target ? [target] : [];
    },
  );
  return {
    self: { id: monster.id, position: observer.point },
    targets,
    allies: context.livingMonsters.flatMap((other) => {
      if (other.id === monster.id || !other.position) return [];
      return [
        {
          id: other.id,
          position: { x: other.position.x, y: other.position.y },
          target: state.monsterMemory.get(other.id)?.target ?? null,
        },
      ];
    }),
    abilities: perceivedAbilitiesFor(state, monster.id),
    casting: castFor(state.combat, monster.id) !== null,
  };
}

/**
 * Everything one summon's brain is allowed to know this tick: the mirror of
 * {@link perceiveDungeonMonster} with the teams reversed. Its targets are the
 * monsters near enough to matter, and its allies are its fellow summons, so a
 * pair of skeletons spreads around a target exactly as a pack does.
 */
function perceiveDungeonSummon(
  world: World,
  state: DungeonCombatState,
  minion: Entity,
  context: {
    level: number;
    monsters: readonly Entity[];
    livingSummons: readonly Entity[];
    sight: (from: BrainPoint, to: BrainPoint) => boolean;
  },
): BrainPerception {
  const position = minion.position!;
  const observer = {
    point: { x: position.x, y: position.y },
    cell: dungeonCellFor(position),
    level: context.level,
    sight: context.sight,
  };
  return {
    self: { id: minion.id, position: observer.point },
    targets: context.monsters.flatMap((candidate) => {
      const target = perceivedTargetFor(world, candidate, observer);
      return target ? [target] : [];
    }),
    allies: context.livingSummons.flatMap((other) => {
      if (other.id === minion.id || !other.position) return [];
      return [
        {
          id: other.id,
          position: { x: other.position.x, y: other.position.y },
          target: state.monsterMemory.get(other.id)?.target ?? null,
        },
      ];
    }),
    abilities: perceivedAbilitiesFor(state, minion.id),
    casting: castFor(state.combat, minion.id) !== null,
  };
}

/**
 * Alert every member of a pack one of whose members has engaged (PRD #794).
 *
 * A pack is the unit a fight is picked with. Whichever way one member noticed
 * the player - proximity, sight, or a hit from outside its detection radius -
 * the rest of its own pack learns about it, so a pack behaves as a group.
 *
 * The propagation stops exactly there. A neighbouring pack is not told anything
 * and activates only on its own perception or its own damage, which is what
 * makes a two-pack pull something the player chose to take on rather than
 * something a chain reaction did to them (user story six). It also means a
 * summoned combatant can pull a pack the same way a player can, because the
 * alert carries whichever attacker the member actually engaged.
 */
function alertEngagedPacks(state: DungeonCombatState): void {
  const living = new Set(state.monsters);
  for (const pack of state.packs.values()) {
    let engaged: BrainMemory | null = null;
    for (const member of pack.members) {
      if (!living.has(member)) continue;
      const memory = state.monsterMemory.get(member);
      if (memory?.target != null && memory.lastKnown) {
        engaged = memory;
        break;
      }
    }
    if (!engaged?.lastKnown || engaged.target === null) continue;
    for (const member of pack.members) {
      if (!living.has(member)) continue;
      const memory = state.monsterMemory.get(member);
      if (!memory || memory.target !== null) continue;
      state.monsterMemory.set(
        member,
        activateMemory(memory, engaged.target, engaged.lastKnown),
      );
    }
  }
}

/**
 * Whether this monster is worth thinking about this tick.
 *
 * An engaged monster always is. A dormant one is only once the player is near
 * enough that it could plausibly notice them, and the radius is a clear margin
 * past the detection radius so the gate can never swallow a detection. This is
 * the whole of what makes a two-hundred-monster floor affordable: everything
 * else about a dormant pack already costs nothing, and this removes the one
 * thing that did not (PRD #794).
 */
function isAttentiveDungeonMonster(
  state: DungeonCombatState,
  monster: Entity,
  playerPosition: { x: number; y: number },
): boolean {
  if (state.monsterMemory.get(monster.id)?.target != null) return true;
  const position = monster.position;
  if (!position) return false;
  return (
    Math.hypot(position.x - playerPosition.x, position.y - playerPosition.y) <=
    DUNGEON_DORMANT_ATTENTION_RADIUS_TILES
  );
}

/**
 * Execute one combatant's movement intent through routing, steering, and
 * teleporter handling. This is the locomotion half the brain seam always
 * promised: the loop that used to own it verbatim now owns only the decision,
 * so a summon walks through exactly the machinery a monster walks through
 * (PRD #804).
 */
function steerDungeonCombatant(
  world: World,
  state: DungeonCombatState,
  candidate: Entity,
  candidateLevel: number,
  intent: BrainIntent,
  context: {
    teleporters: ReturnType<typeof teleportersByCell>;
    repath: boolean;
    elapsedMs: number;
    obstacles: (candidate: Entity, level: number) => {
      x: number;
      y: number;
      radius: number;
    }[];
  },
): void {
  if (!candidate.position || !candidate.mobile) return;
  const goal =
    intent.move.kind === 'approach' || intent.move.kind === 'retreat'
      ? intent.move.point
      : null;
  if (!goal) {
    // `idle` drops the route it was walking; `hold` keeps its place, which is
    // what a combatant standing in its band or committing to a cast does.
    if (intent.move.kind === 'idle') {
      state.monsterRoutes.set(candidate.id, []);
      state.monsterGoals.delete(candidate.id);
    }
    return;
  }

  const candidateCell = dungeonCellFor(candidate.position);
  const goalCell = dungeonCellFor(goal);
  // An approach routes toward the pursued target's level, so a pursuer chasing
  // its target onto a stair follows it. With no live target the goal stays on
  // the mover's own level.
  const target = intent.target === null ? null : world.get(intent.target);
  const targetPosition = target?.position ?? null;
  const targetLevel = targetPosition
    ? world.map.levelAt(
        Math.round(targetPosition.x),
        Math.round(targetPosition.y),
        targetPosition.z,
      )
    : candidateLevel;
  const goalLevel = intent.move.kind === 'approach' ? targetLevel : candidateLevel;
  const previousGoal = state.monsterGoals.get(candidate.id);
  // A route is rebuilt on the shared repath cadence or the moment the brain
  // asks for a different cell, so a reposition takes effect immediately while
  // a pursuit still costs one search every ten ticks.
  if (
    context.repath ||
    !previousGoal ||
    previousGoal.x !== goalCell.x ||
    previousGoal.y !== goalCell.y
  ) {
    const route = findRoute(
      world,
      { ...candidateCell, z: candidateLevel },
      { x: goalCell.x, y: goalCell.y, z: goalLevel },
    );
    const prefix = sameLevelPrefix(route, candidateLevel);
    const fallbackCell = targetPosition ? dungeonCellFor(targetPosition) : goalCell;
    state.monsterRoutes.set(
      candidate.id,
      prefix.length > 0 || sameCell(candidateCell, goalCell)
        ? prefix
        : // The brain asked for a spot nothing can route to - a ring slot
          // inside a wall, say. Falling back to the target's own cell keeps
          // the pursuer closing rather than standing still on a bad slot.
          sameLevelPrefix(
            findRoute(
              world,
              { ...candidateCell, z: candidateLevel },
              { x: fallbackCell.x, y: fallbackCell.y, z: targetLevel },
            ),
            candidateLevel,
          ),
    );
    state.monsterGoals.set(candidate.id, goalCell);
  }
  const obstacles = context.obstacles(candidate, candidateLevel);
  const routed = state.monsterRoutes.get(candidate.id) ?? [];
  // A route is cells, and the brain asked for a point. Standing in the right
  // cell is not standing where it asked, so the last stretch inside one cell
  // is walked as a one-waypoint route to that cell with the exact point as
  // the steering target. Without it a pursuer stops wherever it first
  // entered its slot's cell, which is up to a tile short of contact.
  const candidateRoute =
    routed.length > 0 ||
    Math.hypot(candidate.position.x - goal.x, candidate.position.y - goal.y) <=
      1e-6
      ? routed
      : [goalCell];
  const routeEnd = candidateRoute.at(-1);
  const routeReachesGoal =
    candidateLevel === goalLevel &&
    routeEnd?.x === goalCell.x &&
    routeEnd.y === goalCell.y;
  const moved = advanceDungeonEnemyRoute(
    world,
    candidate.position,
    candidateRoute,
    context.elapsedMs * movementScalarFor(state, candidate.id),
    {
      level: candidateLevel,
      // Steering to the exact continuous point rather than to its cell is
      // what makes a ring slot a place to stand rather than a tile to stack
      // on. The stopping range goes with it: the locomotion layer's default
      // is "stop one tile short of what you are chasing", and a brain-supplied
      // point is already where the brain wants the body, so stopping short of
      // it would put every mover a tile further out than its own band asked
      // for.
      ...(routeReachesGoal ? { target: goal, stoppingRange: 0 } : {}),
      obstacles,
    },
  );
  state.monsterRoutes.set(candidate.id, moved.route);
  const startingCell = dungeonCellFor(candidate.position);
  candidate.position.x = moved.x;
  candidate.position.y = moved.y;
  if (moved.facing !== undefined) {
    candidate.mobile.face(moved.facing);
    candidate.position.facing = moved.facing;
  }
  const movedCell = dungeonCellFor(moved);
  if (
    candidate.mobile.tileX !== movedCell.x ||
    candidate.mobile.tileY !== movedCell.y
  ) {
    candidate.mobile.teleport(movedCell.x, movedCell.y);
    if (moved.facing !== undefined) candidate.mobile.face(moved.facing);
  }
  candidate.position.z = world.map.groundedZ(
    movedCell.x,
    movedCell.y,
    candidateLevel,
  );
  if (startingCell.x !== movedCell.x || startingCell.y !== movedCell.y) {
    const destination = context.teleporters.get(
      teleporterCellKey(movedCell.x, movedCell.y, candidateLevel),
    );
    if (destination) {
      const destinationLevel = world.map.levelAt(
        destination.x,
        destination.y,
        destination.z,
      );
      const settled = advanceDungeonEnemyRoute(
        world,
        destination,
        [],
        0,
        {
          level: destinationLevel,
          obstacles: context.obstacles(candidate, destinationLevel),
        },
      );
      const settledCell = dungeonCellFor(settled);
      candidate.mobile.teleport(settledCell.x, settledCell.y);
      candidate.position.x = settled.x;
      candidate.position.y = settled.y;
      candidate.position.z = world.map.groundedZ(
        settledCell.x,
        settledCell.y,
        destinationLevel,
      );
      state.monsterRoutes.set(candidate.id, []);
      state.monsterGoals.delete(candidate.id);
    }
  }
}

export function stepDungeonCombat(
  world: World,
  state: DungeonCombatState,
  elapsedMs = 50,
): { effects: DungeonCombatEffect[]; playerDead: boolean } {
  restoreDungeonPlayerPosition(world, state);
  state.elapsedTicks += 1;
  // The caster's own clock runs before anything else and regardless of whether
  // the run is still live, which is where the four named cooldown fields used to
  // count down. Aegis expiry is a status expiring, and it needs no effect on the
  // wire: the client renders the buff for the duration it was told at cast time.
  //
  // The events are translated rather than dropped, because a wind-up resolves
  // on a step rather than on the request that started it: a Mage's bolt, its
  // telegraphed lane, and every shot in flight all land here (PRD #800).
  const effects: DungeonCombatEffect[] = translateCastEvents(
    world,
    state,
    stepAbilities(world, state.combat, [state.player]),
  );
  regenerateDungeonVitals(world, state);

  const stillDying = [];
  for (const dying of state.dyingMonsters) {
    dying.remainingTicks -= 1;
    if (dying.remainingTicks === 0) world.remove(dying.id);
    else stillDying.push(dying);
  }
  state.dyingMonsters = stillDying;

  const player = world.get(state.player);
  if (!player?.position || isDead(player) || player.death) {
    syncDungeonProjectileBodies(world, state);
    return { effects, playerDead: true };
  }
  // Sustain is collected by walking over it, so it is settled with the player's
  // own position before anything else moves (PRD #794).
  collectDungeonHealthGlobes(world, state);
  if (state.mode === 'combat-lab' && !state.combatLab?.scenario.active) {
    syncDungeonProjectileBodies(world, state);
    return { effects, playerDead: false };
  }
  state.monsterPathCooldownTicks = Math.max(0, state.monsterPathCooldownTicks - 1);
  // Each monster's own clock: its cooldowns, its statuses, and any cast it has
  // in flight, including the casts of dead casters whose announced hazard is
  // still owed to the player, and every summon's clock beside them, which is
  // where a summon's lifetime counts down and its owner bond is checked (PRD
  // #804). Shots in flight already advanced with the Hero's call: they belong
  // to the world rather than to a group, and one tick is one tick of flight.
  effects.push(
    ...translateCastEvents(
      world,
      state,
      stepAbilities(
        world,
        state.combat,
        [...state.monsters, ...state.summons, ...state.orphanedCasters],
        { projectiles: false },
      ),
    ),
  );
  releaseOrphanedCasters(state);
  if (isDead(player) || player.death) {
    syncDungeonProjectileBodies(world, state);
    return { effects, playerDead: true };
  }
  const playerLevel = world.map.levelAt(
    Math.round(player.position.x),
    Math.round(player.position.y),
    player.position.z,
  );
  // Stable entity id order: a brain's decision may draw from the world rng, so
  // the order they are asked in is part of what a seeded replay reproduces.
  const livingMonsters = [...livingDungeonMonsters(world, state)].sort(
    (left, right) => left.id - right.id,
  );
  // A fully populated floor is mostly asleep. Everything below runs over the
  // monsters that are engaged or near enough to notice the player, and the rest
  // cost one distance comparison each (PRD #794).
  const attentiveMonsters = livingMonsters.filter((candidate) =>
    isAttentiveDungeonMonster(state, candidate, player.position!),
  );
  const livingSummons = [...livingDungeonSummons(world, state)].sort(
    (left, right) => left.id - right.id,
  );
  const teleporters = teleportersByCell(world);
  const repath = state.monsterPathCooldownTicks === 0;
  const playerPosition = player.position;
  const obstaclesAtLevel = (
    candidate: Entity,
    level: number,
  ) => [
    ...(level === playerLevel
      ? [{
          x: playerPosition.x,
          y: playerPosition.y,
          radius: DUNGEON_PLAYER_COLLISION_RADIUS_TILES,
        }]
      : []),
    // Only the neighbourhood: a body four tiles away cannot be collided with
    // this tick, and scanning the whole floor for each mover is what a dense
    // population cannot afford. A summon's body is an obstacle exactly as a
    // monster's is, and to the same neighbourhood rule.
    ...[...attentiveMonsters, ...livingSummons].flatMap((other) => {
      if (other.id === candidate.id || !other.position) return [];
      if (
        candidate.position &&
        Math.hypot(
          other.position.x - candidate.position.x,
          other.position.y - candidate.position.y,
        ) > DUNGEON_OBSTACLE_RADIUS_TILES
      ) {
        return [];
      }
      const otherLevel = world.map.levelAt(
        Math.round(other.position.x),
        Math.round(other.position.y),
        other.position.z,
      );
      return otherLevel === level
        ? [{
            x: other.position.x,
            y: other.position.y,
            radius: DUNGEON_ENEMY_COLLISION_RADIUS_TILES,
          }]
        : [];
    }),
  ];
  const sightByLevel = new Map<number, (from: BrainPoint, to: BrainPoint) => boolean>();
  const sightFor = (level: number) => {
    const cached = sightByLevel.get(level);
    if (cached) return cached;
    const sight = dungeonSightForLevel(world, level);
    sightByLevel.set(level, sight);
    return sight;
  };
  const steerContext = {
    teleporters,
    repath,
    elapsedMs,
    obstacles: obstaclesAtLevel,
  };

  // Decision, then steering, in stable entity id order. The two used to be one
  // fused block, which is why there was exactly one behavior; now the brain
  // answers what this monster wants and the shared steering executes it without
  // knowing which brain answered (PRD #793).
  for (const candidate of attentiveMonsters) {
    if (!candidate.position || !candidate.mobile) continue;
    const candidateLevel = world.map.levelAt(
      Math.round(candidate.position.x),
      Math.round(candidate.position.y),
      candidate.position.z,
    );
    const perception = perceiveDungeonMonster(world, state, candidate, {
      candidateLevel,
      player,
      livingMonsters: attentiveMonsters,
      livingSummons,
      sight: sightFor(candidateLevel),
    });
    const memory = state.monsterMemory.get(candidate.id) ?? createBrainMemory();
    const policy = brain(archetypeFor(state, candidate.id).brain);
    const decision = policy?.decide(perception, memory, world.rng);
    if (decision) state.monsterMemory.set(candidate.id, decision.memory);
    const intent: BrainIntent = decision?.intent ?? {
      target: null,
      desiredRangeTiles: 0,
      move: { kind: 'idle' },
      cast: null,
    };

    if (intent.cast) {
      // The brain asked; the kernel decides. Cooldown, resources, aim legality,
      // and the telegraph are all its business, and a refusal is simply a tick
      // in which this monster did not cast.
      const result = requestCast(
        world,
        state.combat,
        candidate.id,
        intent.cast.abilityId,
        intent.cast.aim,
      );
      if (result.accepted) {
        effects.push(...translateCastEvents(world, state, result.events));
        if (isDead(player) || player.death) {
          syncDungeonProjectileBodies(world, state);
          return { effects, playerDead: true };
        }
      }
    }

    steerDungeonCombatant(world, state, candidate, candidateLevel, intent, steerContext);
  }

  // The summons decide after the monsters, in stable entity id order of their
  // own, reading the same perception shapes with the teams reversed and walking
  // through the same steering (PRD #804). The melee-pursuer they ship with
  // draws no rng, and the order is documented for any brain that does.
  for (const minion of livingSummons) {
    if (!minion.position || !minion.mobile) continue;
    const minionLevel = world.map.levelAt(
      Math.round(minion.position.x),
      Math.round(minion.position.y),
      minion.position.z,
    );
    const perception = perceiveDungeonSummon(world, state, minion, {
      level: minionLevel,
      monsters: attentiveMonsters,
      livingSummons,
      sight: sightFor(minionLevel),
    });
    const memory = state.monsterMemory.get(minion.id) ?? createBrainMemory();
    const policy = brain(state.summonBrains.get(minion.id) ?? '');
    const decision = policy?.decide(perception, memory, world.rng);
    if (decision) state.monsterMemory.set(minion.id, decision.memory);
    const intent: BrainIntent = decision?.intent ?? {
      target: null,
      desiredRangeTiles: 0,
      move: { kind: 'idle' },
      cast: null,
    };
    if (intent.cast) {
      const result = requestCast(
        world,
        state.combat,
        minion.id,
        intent.cast.abilityId,
        intent.cast.aim,
      );
      if (result.accepted) {
        effects.push(...translateCastEvents(world, state, result.events));
      }
    }
    steerDungeonCombatant(world, state, minion, minionLevel, intent, steerContext);
  }
  // Whatever any member noticed this tick, its own pack now knows about, and
  // nothing outside that pack does.
  alertEngagedPacks(state);
  if (repath) {
    state.monsterPathCooldownTicks = DUNGEON_MONSTER_REPATH_TICKS;
  }

  syncDungeonProjectileBodies(world, state);
  return { effects, playerDead: false };
}

export interface DungeonCombatView {
  /** Present only for the focused tuning room. */
  mode?: 'combat-lab';
  /** Authored setup currently running in the focused tuning room. */
  labScenario?: Pick<CombatLabScenario, 'id' | 'label' | 'description'>;
  classId: HeroClassId;
  level: number;
  xp: number;
  hp: number;
  maxHp: number;
  mana: number;
  maxMana: number;
  /**
   * Remaining cooldown ticks per ability id, carrying an entry for every ability
   * in the loadout so a reader never has to know which abilities exist. It
   * replaces the three named fields, which could not survive per-ability
   * cooldowns: Whirlwind and Piercing Thrust had no field of their own.
   */
  cooldowns: Record<AbilityId, number>;
  /**
   * Remaining ticks of the longest timed effect running on the Hero, which the
   * HUD renders as the buff bar. Deliberately not keyed by status id: no
   * consumer inspects a status by name.
   */
  buffRemainingTicks?: number;
  kills: number;
  backpack: ArpgItem[];
  ability: AbilityId;
  abilities?: AbilityId[];
  mainHandGrip?: WeaponGrip;
  movement?: {
    seq: number;
    elapsedMs: number;
    x: number;
    y: number;
    facing: Direction;
    chargeRemainingMs: number;
    /**
     * The status fold's movement scalar for the Hero, so client prediction
     * walks at the speed the server walks and Wraith Form reads as speed
     * rather than as rubber-banding (PRD #804). Absent means one.
     */
    moveSpeedScalar?: number;
  };
  monster?: {
    id: EntityId;
    hp: number;
    maxHp: number;
  };
  /** The full passive pack in the combat lab. */
  monsters?: {
    id: EntityId;
    hp: number;
    maxHp: number;
  }[];
}

/** Every loadout ability's remaining cooldown, zero included, in loadout order. */
export function dungeonAbilityCooldowns(
  state: DungeonCombatState,
): Record<AbilityId, number> {
  const caster = combatantFor(state.combat.combatants, state.player);
  const cooldowns: Record<AbilityId, number> = {};
  for (const abilityId of state.abilities) {
    cooldowns[abilityId] = caster ? cooldownRemaining(caster, abilityId) : 0;
  }
  return cooldowns;
}

/** How long the Hero's longest-running timed effect has left. */
function dungeonBuffRemainingTicks(state: DungeonCombatState): number {
  const caster = combatantFor(state.combat.combatants, state.player);
  return (caster?.statuses ?? []).reduce(
    (longest, status) => Math.max(longest, status.remainingTicks),
    0,
  );
}

export function dungeonCombatView(
  world: World,
  state: DungeonCombatState,
): DungeonCombatView {
  const health = world.get(state.player)?.health;
  const monster = world.get(state.monster)?.health;
  const monsterViews = state.monsters.flatMap((id) => {
    const candidate = world.get(id)?.health;
    return candidate
      ? [{ id, hp: candidate.current, maxHp: candidate.max }]
      : [];
  });
  return {
    ...(state.mode === 'combat-lab' ? { mode: state.mode } : {}),
    ...(state.combatLab
      ? {
          labScenario: {
            id: state.combatLab.scenario.id,
            label: state.combatLab.scenario.label,
            description: state.combatLab.scenario.description,
          },
        }
      : {}),
    classId: state.classId,
    level: state.level,
    xp: state.xp,
    hp: health?.current ?? 0,
    maxHp: health?.max ?? state.stats.maxHealth,
    mana: state.mana,
    maxMana: state.stats.maxMana,
    cooldowns: dungeonAbilityCooldowns(state),
    buffRemainingTicks: dungeonBuffRemainingTicks(state),
    kills: state.kills,
    backpack: state.backpack.map((item) => ({ ...item })),
    ability: state.ability,
    abilities: state.abilities,
    mainHandGrip: state.stats.mainHandGrip,
    movement: {
      seq: state.movement.seq,
      elapsedMs: state.movement.elapsedMs,
      x: state.playerPosition.x,
      y: state.playerPosition.y,
      facing:
        world.get(state.player)?.position?.facing ??
        world.get(state.player)?.mobile?.facing ??
        Direction.S,
      chargeRemainingMs: state.movement.chargeRemainingMs,
      moveSpeedScalar: movementScalarFor(state, state.player),
    },
    ...(monster
      ? {
          monster: {
            id: state.monster,
            hp: monster.current,
            maxHp: monster.max,
          },
        }
      : {}),
    ...(state.mode === 'combat-lab' ? { monsters: monsterViews } : {}),
  };
}
